import React, { useState, useEffect, useCallback, useRef, createContext, useContext, ReactNode, lazy, Suspense } from "react";
const LOIEditor = lazy(() => import("./LOIEditor"));
const LOITemplateEditor = lazy(() => import("./LOITemplateEditor").catch(err => {
  console.error("Failed to load LOITemplateEditor:", err);
  return { default: ({ onClose }: { onClose: () => void }) => (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "white", padding: 32, borderRadius: 12, maxWidth: 500, textAlign: "center" }}>
        <p style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Failed to load Template Editor</p>
        <p style={{ color: "#666", fontSize: 13, marginBottom: 16 }}>{String(err)}</p>
        <button onClick={onClose} style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: "#002767", color: "white", cursor: "pointer", fontWeight: 600 }}>Close</button>
      </div>
    </div>
  )} as any;
}));
import { api, standaloneApi } from "./api/client";
import type { InvestorSettings, LOITemplate, GHLCustomField, StandaloneUser, AppContact, RawExtractionSnapshot, AnalysisStartBody, AnalysisTrace } from "./api/client";
import AuthScreen from "./AuthScreen";
import {
  Building2, Upload, FileText, ChevronRight, ChevronLeft, CheckCircle2,
  AlertCircle, X, Download, Eye, BarChart3, Shield,
  TrendingUp, Target, Loader2, Settings, FileCheck,
  DollarSign, ArrowRight, Save, Send, FilePlus,
  Briefcase, Zap, AlertTriangle, BookOpen, Scale,
  ChevronDown, ChevronUp, Home, FolderOpen,
  ClipboardList, LucideIcon, Search, Plus, Link2, Tag, RefreshCw, Trash2, Archive, Pencil, Info
} from "lucide-react";

/* ═══════════════════════════════════════════════
   TYPES
   ═══════════════════════════════════════════════ */
type PropertyType = "single" | "multi";
type ToastType = "success" | "error" | "warning";
type BadgeVariant = "default" | "success" | "warning" | "error" | "info";
type BtnVariant = "primary" | "danger" | "ghost";

const APP_ENV = (import.meta as unknown as { env: Record<string, string | undefined> }).env;
const SINGLE_FAMILY_ENABLED = APP_ENV.VITE_ENABLE_SINGLE_FAMILY === "true";
// Internal Analysis Log visibility.
// Durable, per-browser toggle so it can be granted to specific people (e.g. Jared)
// and locked down again later WITHOUT a redeploy:
//   • open the app once with ?debugTrace=1  → log stays ON for this browser
//   • open the app once with ?debugTrace=0  → log turns OFF for this browser
//   • global default is OFF unless VITE_SHOW_ANALYSIS_TRACE=true at build time
const TRACE_FLAG_KEY = "rei_debug_trace";
(() => {
  try {
    const q = new URLSearchParams(window.location.search).get("debugTrace");
    if (q === "1") localStorage.setItem(TRACE_FLAG_KEY, "1");
    else if (q === "0") localStorage.removeItem(TRACE_FLAG_KEY);
  } catch { /* localStorage unavailable */ }
})();
const SHOW_ANALYSIS_TRACE = APP_ENV.VITE_SHOW_ANALYSIS_TRACE === "true"
  || (() => { try { return localStorage.getItem(TRACE_FLAG_KEY) === "1"; } catch { return false; } })();

interface Contact {
  id: string;
  name: string;
  email: string;
  company: string;
}

interface Toast {
  id: number;
  msg: string;
  type: ToastType;
}

interface UploadedFile {
  id: number;
  name: string;
  size: number;
  progress: number;
  status: "uploading" | "complete";
  docType: string | null;
  category: "photo" | "document";
}

interface ReviewFieldsSubmitPayload {
  fields: Record<string, string>;
  rawExtraction: RawExtractionSnapshot | null;
}

interface DocRequirement {
  key: string;
  label: string;
  req: boolean;
  icon: LucideIcon;
}



interface ReportRecord {
  id: string;
  property: string;
  address: string;
  units: number;
  tier2: string;
  fdts: string;
  createdAt: string;
  type: PropertyType;
}

interface LOIRecord {
  id: string;
  address: string;
  offerPrice: string;
  status: string;
  template: string;
  createdAt: string;
}

interface ReportField {
  l: string;
  v: string;
}

interface TierData {
  name: string;
  irr: string;
  price: string;
  perUnit: string;
  color: string;
  rec?: boolean;
}

interface ReportSectionData {
  type: "fields" | "prose" | "tiers";
  title: string;
  fields?: ReportField[];
  content?: string;
  tiers?: TierData[];
  wt?: { price: string; verdict: string };
}

interface ReportSection {
  key: string;
  label: string;
  icon: LucideIcon;
}

interface CRMField {
  key: string;
  label: string;
  val: string;
}

interface ProcessStep {
  key: string;
  label: string;
  num: string;
  icon: LucideIcon;
  desc: string;
}

/* ═══════════════════════════════════════════════
   BRAND TOKENS — navy #002767 | red #BD0C31
   ═══════════════════════════════════════════════ */
const C = {
  navy: "#002767", red: "#BD0C31", navyLight: "#003a99",
  navyFaint: "#e8eef7", navyBg: "#f3f6fb", redFaint: "#fbeaee",
  g50: "#f8f9fb", g100: "#eef1f6", g200: "#dce1ea", g300: "#b8c1d1",
  g400: "#8994a9", g500: "#5f6b80", g600: "#3e4a5e", g700: "#283547",
  g800: "#1a2435", g900: "#0f1724",
} as const;

/* ═══════════════════════════════════════════════
   TOAST
   ═══════════════════════════════════════════════ */
const ToastCtx = createContext<((msg: string, type?: ToastType) => void) | null>(null);

function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const fire = useCallback((msg: string, type: ToastType = "success") => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { id, msg, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 4000);
  }, []);
  return (
    <ToastCtx.Provider value={fire}>{children}
      <div className="fixed bottom-6 right-6 z-[200] flex flex-col gap-2.5 pointer-events-none">
        {toasts.map(t => (
          <div key={t.id} style={{ background: t.type === "success" ? "#059669" : t.type === "error" ? C.red : "#d97706" }}
            className="pointer-events-auto px-5 py-3.5 rounded-2xl shadow-2xl text-[15px] font-bold flex items-center gap-2.5 text-white animate-toastIn">
            {t.type === "success" ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}{t.msg}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

const useToast = (): ((msg: string, type?: ToastType) => void) => {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be inside ToastProvider");
  return ctx;
};

/* ═══════════════════════════════════════════════
   API CONTEXT — switches between GHL api and standalone appApi
   ═══════════════════════════════════════════════ */
type ApiAdapter = typeof api;
const ApiCtx = createContext<ApiAdapter>(api);
const useApi = (): ApiAdapter => useContext(ApiCtx);
const AppModeCtx = createContext<boolean>(false); // true = standalone, false = GHL
const useIsAppMode = (): boolean => useContext(AppModeCtx);

/* ═══════════════════════════════════════════════
   CRM CONTACT CONTEXT
   Handles GHL iframe injection:
   - ?locationId=X&contactId=Y (standard)
   - ?ssoKey=ENCRYPTED (marketplace SSO)
   ═══════════════════════════════════════════════ */
function useCRMContext(): { contact: Contact; authStatus: "loading" | "connected" | "disconnected" | "expired"; installUrl: string | null; hasContext: boolean } {
  const [contact, setContact] = useState<Contact>({
    id: "", name: "",
    email: "", company: "",
  });
  const [authStatus, setAuthStatus] = useState<"loading" | "connected" | "disconnected" | "expired">("loading");
  const [installUrl, setInstallUrl] = useState<string | null>(null);
  const [hasContext, setHasContext] = useState(false);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const ssoKey = p.get("ssoKey");
    const urlContactId = p.get("contactId") || p.get("contact_id");
    const urlLocationId = p.get("locationId") || p.get("location_id");

    // Read contact info from URL params (GHL passes these in the iframe URL)
    const urlName = p.get("full_name") || [p.get("first_name"), p.get("last_name")].filter(Boolean).join(" ") || "";
    const urlEmail = p.get("email") || "";
    const urlCompany = p.get("company_name") || "";

    const init = async () => {
      let contactId = urlContactId;
      let locationId = urlLocationId;

      // Set contact from URL params immediately
      if (contactId) {
        setContact({ id: contactId, name: urlName, email: urlEmail, company: urlCompany });
      }
      if (contactId && locationId) {
        setHasContext(true);
      }

      // Decrypt SSO key if present (GHL marketplace iframe)
      if (ssoKey) {
        try {
          const sso = await api.decryptSSO(ssoKey);
          if (sso.locationId) locationId = sso.locationId;
          if (sso.contactId) contactId = sso.contactId;
          // Update URL params so API client picks up locationId
          const url = new URL(window.location.href);
          if (locationId) url.searchParams.set("locationId", locationId);
          if (contactId) url.searchParams.set("contactId", contactId);
          window.history.replaceState({}, "", url.toString());
          if (contactId && locationId) {
            setHasContext(true);
          }
        } catch {
          // SSO decrypt failed — continue with URL params
        }
      }

      // Check auth status
      if (locationId) {
        try {
          const status = await api.getAuthStatus(locationId, window.location.href);
          if (status.connected && !status.expired) {
            setAuthStatus("connected");
          } else if (status.expired) {
            setAuthStatus("expired");
            setInstallUrl(status.installUrl || null);
            // Try auto-refresh
            try {
              await api.refreshToken(locationId);
              setAuthStatus("connected");
            } catch {
              // refresh failed — stay expired
            }
          } else {
            setAuthStatus("disconnected");
            setInstallUrl(status.installUrl || null);
          }
        } catch {
          setAuthStatus("disconnected");
        }
      } else {
        // Dev mode — no locationId needed
        setAuthStatus("connected");
      }

      // Fetch contact from API to enrich URL param data
      if (contactId && locationId) {
        setHasContext(true);
        try {
          const { contact: c } = await api.getContact(contactId);
          setContact({ id: c.id, name: c.name || urlName, email: c.email || urlEmail, company: c.company || urlCompany });
        } catch {
          // Keep URL param data as fallback — already set above
        }
      } else if (!contactId && !locationId) {
        // Dev mode — allow without context
        setHasContext(true);
      }
    };

    init();

    const h = (e: MessageEvent) => {
      if (e.data?.type === "crm_contact_context" && e.data.contact) setContact(e.data.contact);
    };
    window.addEventListener("message", h);
    return () => window.removeEventListener("message", h);
  }, []);

  return { contact, authStatus, installUrl, hasContext };
}

/* ═══════════════════════════════════════════════
   SHARED UI
   ═══════════════════════════════════════════════ */
function Badge({ children, variant = "default", className = "" }: { children: ReactNode; variant?: BadgeVariant; className?: string }) {
  const bg: Record<BadgeVariant, string> = { default: C.navyFaint, success: "#ecfdf5", warning: "#fffbeb", error: C.redFaint, info: "#eff6ff" };
  const fg: Record<BadgeVariant, string> = { default: C.navy, success: "#065f46", warning: "#92400e", error: C.red, info: "#1e40af" };
  const bd: Record<BadgeVariant, string> = { default: "transparent", success: "#a7f3d0", warning: "#fde68a", error: "#fca5a5", info: "#bfdbfe" };
  return <span style={{ background: bg[variant], color: fg[variant], border: `1px solid ${bd[variant]}` }} className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[12px] font-semibold tracking-wide ${className}`}>{children}</span>;
}

function Card({ children, className = "", style = {} }: { children: ReactNode; className?: string; style?: React.CSSProperties }) {
  return <div className={`bg-white rounded-2xl shadow-sm ${className}`} style={{ border: `1px solid ${C.g200}`, ...style }}>{children}</div>;
}

function Btn({ children, variant = "primary", disabled, onClick, className = "", style = {} }: { children: ReactNode; variant?: BtnVariant; disabled?: boolean; onClick?: () => void; className?: string; style?: React.CSSProperties }) {
  const s: React.CSSProperties = variant === "primary"
    ? { background: disabled ? C.g300 : C.navy, color: "white", boxShadow: disabled ? "none" : `0 4px 16px ${C.navy}30`, ...style }
    : variant === "danger" ? { background: C.red, color: "white", ...style }
      : { background: "white", color: C.g600, border: `1px solid ${C.g200}`, ...style };
  return <button onClick={onClick} disabled={disabled} style={s} className={`inline-flex items-center justify-center gap-2.5 font-semibold rounded-xl transition-all px-6 py-3 text-[15px] ${disabled ? "opacity-40 cursor-not-allowed" : "hover:opacity-90"} ${className}`}>{children}</button>;
}

function Toggle({ state, set }: { state: boolean; set: (v: boolean) => void }) {
  return <button onClick={() => set(!state)} className="w-12 h-7 rounded-full relative transition-all flex-shrink-0" style={{ background: state ? C.navy : C.g300 }}>
    <div className="absolute top-1.5 w-4 h-4 bg-white rounded-full shadow transition-all" style={{ left: state ? 26 : 4 }} />
  </button>;
}

function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse rounded-xl ${className}`} style={{ background: C.g100 }} />;
}

/* LOI templates now fetched from API — see LOIStep component.
   Reports & LOIs are fetched live from the API in ReportsTab / LOIsTab. */

const MF_DOCS: DocRequirement[] = [
  { key: "rr", label: "Rent Roll (RR)", req: true, icon: BarChart3 }, { key: "t12", label: "T12 / Trailing 12", req: true, icon: TrendingUp },
  { key: "om", label: "Offering Memorandum", req: false, icon: FileText }, { key: "broker_notes", label: "Broker Notes / Quotes", req: false, icon: BookOpen },
  { key: "tax_docs", label: "Tax Bill / Assessment", req: false, icon: Scale }, { key: "insurance", label: "Insurance History", req: false, icon: Shield },
  { key: "capex_sheet", label: "CapEx Spend Sheet", req: false, icon: Zap }, { key: "photos", label: "Property Photos", req: false, icon: Eye },
  { key: "ar_report", label: "AR / Collections", req: false, icon: DollarSign }, { key: "debt_quotes", label: "Debt Quotes", req: false, icon: Building2 },
];
const SF_DOCS: DocRequirement[] = [
  { key: "photos", label: "Property Photos", req: false, icon: Eye }, { key: "listing", label: "Listing Sheet / MLS Export", req: false, icon: FileText },
  { key: "inspection", label: "Inspection Reports", req: false, icon: ClipboardList }, { key: "comps", label: "Comparable Sales", req: false, icon: TrendingUp },
  { key: "tax_docs", label: "Tax Records", req: false, icon: Scale }, { key: "seller_disclosure", label: "Seller Disclosure", req: false, icon: BookOpen },
  { key: "lender_quote", label: "Lender Quote", req: false, icon: DollarSign }, { key: "contractor_est", label: "Contractor Estimate", req: false, icon: Zap },
  { key: "lease_stmt", label: "Lease / Rent Statement", req: false, icon: FileCheck }, { key: "insurance", label: "Insurance Quote", req: false, icon: Shield },
];

const CRM_FIELDS_MF: CRMField[] = [
  { key: "property_name", label: "Property Name", val: "Grand Reserve Apartments" }, { key: "address", label: "Address", val: "Ocala, FL" },
  { key: "units", label: "Number of Units", val: "263" }, { key: "asset_class", label: "Asset Class", val: "B" },
  { key: "occupancy", label: "Economic Occupancy", val: "87.1%" }, { key: "noi", label: "T12 NOI", val: "$2,929,804" },
  { key: "asking_price", label: "Asking Price", val: "$54,000,000" }, { key: "tier3_offer", label: "Tier 3 Offer", val: "$44,025,726" },
  { key: "tier2_offer", label: "Tier 2 Offer", val: "$46,568,472" }, { key: "tier1_offer", label: "Tier 1 Offer", val: "$48,735,974" },
  { key: "deal_type", label: "Deal Type", val: "Moderate Value-Add" }, { key: "exit_strategies", label: "Exit Strategies", val: "Value-Add & Hold, Stabilize & Sell" },
];
const CRM_FIELDS_SF: CRMField[] = [
  // ── 1. Property Identity ──
  { key: "property_address", label: "Property Address", val: "" },
  { key: "city", label: "Property City", val: "" },
  { key: "state", label: "Property State", val: "" },
  { key: "zip", label: "Zip", val: "" },
  { key: "property_type", label: "Property Type", val: "" },
  // ── 2. Core Property Info ──
  { key: "beds_baths", label: "Beds / Baths", val: "" },
  { key: "sqft", label: "Square Feet", val: "" },
  { key: "lot_size", label: "Lot Size", val: "" },
  { key: "year_built", label: "Year Built", val: "" },
  { key: "occupancy_status", label: "Occupancy Status", val: "" },
  { key: "current_rent", label: "Current Rent", val: "" },
  { key: "asking_price", label: "Asking Price", val: "" },
  { key: "seller_ask", label: "Seller Ask", val: "" },
  { key: "price_flexibility", label: "Price Flexibility", val: "" },
  // ── 3. Condition / Rehab ──
  { key: "condition", label: "Condition / Repairs Needed", val: "" },
  { key: "rehab_estimate", label: "Rehab Estimate", val: "" },
  { key: "contractor_estimate", label: "Contractor Estimate", val: "" },
  // ── 4. Value & Comps ──
  { key: "arv_estimate", label: "ARV Estimate", val: "" },
  { key: "sales_comp_1", label: "Sales Comp 1", val: "" },
  { key: "sales_comp_2", label: "Sales Comp 2", val: "" },
  { key: "sales_comp_3", label: "Sales Comp 3", val: "" },
  { key: "rent_estimate", label: "Rent Estimate", val: "" },
  { key: "rent_benchmark", label: "Rent Zestimate / Rent Benchmark", val: "" },
  // ── 5. Financing & Mortgage ──
  { key: "active_mortgage", label: "Active Mortgage?", val: "" },
  { key: "mortgage_payoff", label: "Mortgage Payoff", val: "" },
  { key: "monthly_mortgage_pmt", label: "Monthly Mortgage Payment", val: "" },
  { key: "pmt_includes_ti", label: "Monthly Payment Includes Taxes & Insurance?", val: "" },
  { key: "mortgage_current", label: "Mortgage Payments Current?", val: "" },
  { key: "open_to_terms", label: "Open to Creative Terms", val: "" },
  { key: "open_to_subject_to", label: "Open to Subject-To", val: "" },
  { key: "lender_quote", label: "Lender Quote", val: "" },
  // ── 6. Taxes / Insurance / Carrying ──
  { key: "annual_taxes", label: "Annual Property Taxes", val: "" },
  { key: "annual_insurance", label: "Insurance Quote", val: "" },
  { key: "hoa_fee", label: "HOA Fee", val: "" },
  { key: "hoa_details", label: "HOA Details", val: "" },
  { key: "owner_paid_utilities", label: "Owner-Paid Utilities / Fees", val: "" },
  { key: "mgmt_percent", label: "Management % Assumption", val: "" },
  { key: "maintenance_percent", label: "Maintenance / Reserves %", val: "" },
  // ── 7. Legal / Record-Risk ──
  { key: "public_record_beds", label: "Public Record Beds", val: "" },
  { key: "public_record_baths", label: "Public Record Baths", val: "" },
  { key: "public_record_sqft", label: "Public Record Square Feet", val: "" },
  { key: "functional_beds", label: "Functional Beds", val: "" },
  { key: "functional_baths", label: "Functional Baths", val: "" },
  { key: "functional_sqft", label: "Functional Square Feet", val: "" },
  { key: "record_mismatch", label: "Record Mismatch?", val: "" },
  { key: "legal_permit_notes", label: "Legal / Permit Notes", val: "" },
  { key: "seller_disclosure", label: "Seller Disclosure Notes", val: "" },
  { key: "liens_owed", label: "Contractors or Liens Owed", val: "" },
  // ── 8. Deal Context ──
  { key: "listing_description", label: "Listing Description / Inquiry Notes", val: "" },
  { key: "seller_notes", label: "Reason for Selling", val: "" },
  { key: "acquisition_lane", label: "Acquisition Lane", val: "" },
  { key: "days_on_market", label: "Days on Market", val: "" },
  { key: "price_reductions", label: "Price Reductions", val: "" },
  { key: "listed_with_realtor", label: "Listed with Realtor?", val: "" },
  { key: "listed_on_mls", label: "Listed on MLS?", val: "" },
  { key: "propstream_snapshot", label: "PropStream Snapshot / Public Data Notes", val: "" },
  { key: "deal_notes", label: "Deal Notes", val: "" },
  // ── 9. Major System Flags ──
  { key: "roof_notes", label: "Roof", val: "" },
  { key: "hvac_notes", label: "HVAC", val: "" },
  { key: "foundation_notes", label: "Foundation", val: "" },
  { key: "plumbing_notes", label: "Plumbing / Sewer", val: "" },
  { key: "electrical_notes", label: "Electrical", val: "" },
  { key: "window_notes", label: "Windows", val: "" },
  { key: "drainage_notes", label: "Drainage / Grading", val: "" },
  { key: "envelope_notes", label: "Exterior Envelope / Siding", val: "" },
  { key: "hidden_systems_risk", label: "Hidden Systems Risk", val: "" },
  // ── 10. Analysis Output ──
  { key: "strategy", label: "Strategy", val: "" },
  { key: "pricing_posture", label: "Pricing Posture", val: "" },
  { key: "tier3_offer", label: "Tier 3 Offer", val: "" },
  { key: "tier2_offer", label: "Tier 2 Offer", val: "" },
  { key: "tier1_offer", label: "Tier 1 Offer", val: "" },
  { key: "monthly_cash_flow", label: "Monthly Cash Flow (T2)", val: "" },
  { key: "deal_type", label: "Deal Type", val: "" },
  { key: "investor_verdict", label: "Investor Verdict", val: "" },
];

const MF_SECTIONS: ReportSection[] = [
  { key: "summary", label: "Property Summary", icon: FileText }, { key: "truth_deck", label: "Truth Deck", icon: BookOpen },
  { key: "deal_class", label: "Classification", icon: Building2 }, { key: "rent_comps", label: "Rent Comps", icon: TrendingUp },
  { key: "tax", label: "Tax & Insurance", icon: Shield }, { key: "capex", label: "CapEx Scope", icon: Zap },
  { key: "financing", label: "Financing", icon: Scale }, { key: "tiers", label: "Offer Tiers", icon: DollarSign },
  { key: "constraints", label: "Constraints", icon: AlertTriangle }, { key: "recommendation", label: "Recommendation", icon: Target },
];
const SF_SECTIONS: ReportSection[] = [
  { key: "summary", label: "Property Summary", icon: FileText },
  { key: "sources", label: "Data Sources", icon: Info },
  { key: "record_mismatch", label: "Record Mismatch", icon: AlertTriangle },
  { key: "strategy", label: "Strategy", icon: Target },
  { key: "acquisition_lane", label: "Acquisition Lane", icon: Briefcase },
  { key: "competition", label: "Competition", icon: TrendingUp },
  { key: "location", label: "Micro-Location", icon: Home },
  { key: "sales_comps", label: "Sales Comps", icon: TrendingUp },
  { key: "rent_comps", label: "Rent Comps", icon: DollarSign },
  { key: "rehab", label: "Rehab / Condition", icon: Zap },
  { key: "hidden_risk", label: "Hidden Risk", icon: Shield },
  { key: "red_flags", label: "Red Flags", icon: AlertCircle },
  { key: "financing", label: "Financing", icon: Scale },
  { key: "tax", label: "Taxes & Insurance", icon: Shield },
  { key: "value_split", label: "Value Split", icon: BarChart3 },
  { key: "valuation_stack", label: "Valuation Stack", icon: BarChart3 },
  { key: "payment_check", label: "Payment Check", icon: DollarSign },
  { key: "cash_flow", label: "Cash Flow", icon: DollarSign },
  { key: "brrrr", label: "BRRRR Snapshot", icon: Zap },
  { key: "frozen_snapshot", label: "Frozen Assumptions", icon: FileCheck },
  { key: "tiers", label: "Offer Tiers", icon: DollarSign },
  { key: "constraints", label: "Constraints", icon: AlertTriangle },
  { key: "recommendation", label: "Verdict", icon: Target },
];

const MF_REPORT: Record<string, ReportSectionData> = {
  summary: {
    type: "fields", title: "Property Report Summary", fields: [
      { l: "Property Name", v: "Grand Reserve Apartments" }, { l: "Address", v: "Ocala, FL" }, { l: "Units", v: "263" }, { l: "Vintage", v: "2003" },
      { l: "Economic Occupancy", v: "87.1% (229/263)" }, { l: "Asking", v: "$54,000,000" }, { l: "T12 Revenue", v: "$4,962,940" },
      { l: "T12 NOI", v: "$2,929,804" }, { l: "T12 Taxes", v: "$631,924" }, { l: "T12 Insurance", v: "$203,836" },
    ]
  },
  truth_deck: { type: "prose", title: "Truth Deck", content: "RR is primary truth. T12 for income/expenses. OM is context only.\n\n• Econ occ: 87.1% (229/263)\n• Avg rent: $1,644/mo, market rent: $1,844/mo\n• T12 NOI: $2,929,804\n• AR delinquency: $62,397 across 24 residents" },
  deal_class: { type: "prose", title: "Classification", content: "FDTS Score: 5–6 → Moderate Value-Add\nAsset Class: B (Crime A- / Asset B+)\n\n1. 87% occupancy gap\n2. $62k delinquency\n3. ~118 classic units remaining" },
  rent_comps: { type: "prose", title: "Rent Comps", content: "1 BR: $1,400–$1,525\n2 BR: $1,550–$1,750\n3 BR: $1,800–$2,000\n\nRenovation premium: $250/mo" },
  tax: { type: "prose", title: "Tax & Insurance", content: "Tax: 1.916% effective rate, resets on sale\nInsurance base: $1,050/door, downside: $1,300/door" },
  capex: { type: "prose", title: "CapEx Scope", content: "Interior: 118 units × $14k = $1.65M\nExterior: $394k\nContingency: 15%\nTotal: ~$2.36M" },
  financing: { type: "prose", title: "Financing", content: "Indicative agency lane (UNVERIFIED):\nBase: 65% LTV, ~5.50%, 30yr\nConservative: 5.75%\nDSCR: 1.25x | GREEN" },
  tiers: {
    type: "tiers", title: "Offer Tiers", tiers: [
      { name: "Tier 3 — Initial", irr: "≥18% Conservative", price: "$44,025,726", perUnit: "$167,400", color: "green" },
      { name: "Tier 2 — BOF", irr: "≥18% Realistic", price: "$46,568,472", perUnit: "$177,100", color: "navy", rec: true },
      { name: "Tier 1 — Stretch", irr: "≥16% Realistic", price: "$48,735,974", perUnit: "$185,300", color: "amber" },
    ], wt: { price: "$54,000,000", verdict: "Fragile — taxes reset to ~$1.035M/yr, cap ~4.55%" }
  },
  constraints: { type: "prose", title: "Constraints", content: "1. Tax reset (Florida)\n2. Occupancy gap (87%→93–95%)\n3. Insurance repricing\n4. Renovation pace\n5. Exit cap risk" },
  recommendation: { type: "prose", title: "Recommendation", content: "Anchor at Tier 2 ($46.6M). Stretch only if:\n1. Insurance controllable\n2. Classic count ≤45%\n3. Occ issue is operational" },
};

const SF_REPORT: Record<string, ReportSectionData> = {
  summary: {
    type: "fields", title: "Property Summary", fields: [
      { l: "Address", v: "847 Maple Dr, Evanston IL" }, { l: "Bed/Bath", v: "4 BD / 2.5 BA" }, { l: "Sq Ft", v: "2,850" },
      { l: "Year Built", v: "2003" }, { l: "Asking", v: "$485,000" }, { l: "ARV", v: "$545,000" }, { l: "Condition", v: "Fair" },
    ]
  },
  sources: { type: "prose", title: "Data Sources / Assumptions", content: "Photos / condition: AI-estimated from uploaded photos plus hidden-system assumptions.\nARV: estimated from comparable sales and available property facts.\nRent: estimated from rental comps and model assumptions.\nOffer range: calculated from valuation, rehab, financing, taxes/insurance, and investor return criteria.\nTarget return: Default/settings tier criteria." },
  record_mismatch: { type: "prose", title: "Record Mismatch / Legal-Layout Review", content: "Public records show 3BD/2BA, listing shows 4BD/2.5BA. Verify with county records — potential unpermitted addition." },
  strategy: { type: "prose", title: "Strategy Classification", content: "Fix & Flip — cosmetic rehab with strong ARV upside.\nBRRRR viable as secondary strategy if refi at 75% LTV." },
  acquisition_lane: { type: "prose", title: "Acquisition Lane", content: "On-market MLS listing, 45 DOM. Seller motivated — price reduced $15k last week. Direct-to-agent negotiation preferred." },
  competition: { type: "prose", title: "Competition / Offer Pressure", content: "Low competition — 45 DOM with price reduction. No backup offers reported. Favorable negotiation position." },
  location: { type: "prose", title: "Micro-Location Assessment", content: "B+ neighborhood, strong school district. Median home price $520k. Low crime index. Walkable to downtown shops." },
  sales_comps: { type: "prose", title: "Sales Comp Analysis", content: "• 820 Oak St — $510k (renovated)\n• 903 Elm Ave — $475k (avg condition)\n• 711 Pine Dr — $530k (updated)\n\nMedian: $510k | Adjusted ARV: $545k" },
  rent_comps: { type: "prose", title: "Rent Comp Analysis", content: "4BD comps: $2,100–$2,400/mo\nMedian: $2,250/mo\nRenovated premium: +$150–200/mo\nEstimate post-rehab: $2,350/mo" },
  rehab: { type: "prose", title: "Rehab / Condition Assessment", content: "Kitchen: $12–15k\nBaths: $8–10k\nFlooring: $6–8k\nPaint: $4–6k\nLandscaping: $3–5k\nTotal: $35–45k\nCondition: Fair — cosmetic only, no structural." },
  hidden_risk: { type: "prose", title: "Hidden Systems Risk", content: "Roof: ~10 yrs remaining. HVAC: 2015 install, functional. Plumbing: copper supply, cast-iron main — scope for replacement in 5–7 yrs." },
  red_flags: { type: "prose", title: "Red-Flag Override", content: "Potential unpermitted 4th bedroom addition. Verify with building dept before closing. Could affect ARV if not up to code." },
  financing: { type: "prose", title: "Financing Lane Selection", content: "Hard money: 85% LTC, 12%, 12-mo term — best for flip.\nDSCR loan: 75% LTV, 7.5%, 30yr — best for hold.\nConventional: 80% LTV if owner-occ eligible." },
  tax: { type: "prose", title: "Taxes & Insurance", content: "Annual taxes: $6,200 (1.28% of assessed value)\nInsurance: ~$1,800/yr (est.)\nNo HOA." },
  value_split: { type: "prose", title: "Retail vs Investor Value Split", content: "Retail buyer value: $530–545k (move-in ready ARV)\nInvestor value: $440–460k (accounting for rehab + profit margin)\nSpread: $70–105k" },
  valuation_stack: { type: "prose", title: "Valuation Stack", content: "Comp-based ARV: $545k\n70% Rule max: $346k\nIncome approach: $470k (at 6% cap)\nAs-is FMV: $460–475k" },
  payment_check: { type: "prose", title: "Monthly Payment Reality Check", content: "At $450k purchase, 75% LTV, 7.5%:\nP&I: $2,360/mo\nTaxes: $517/mo\nInsurance: $150/mo\nTotal PITI: $3,027/mo\nRent: $2,350/mo → negative $677/mo w/o value-add" },
  cash_flow: { type: "prose", title: "Cash Flow at Ask / Tier 2 / Tier 3", content: "At Ask ($485k): -$890/mo\nAt Tier 2 ($450k): -$677/mo\nAt Tier 3 ($415k): -$410/mo\nFlip strategy recommended over hold at current rates." },
  brrrr: { type: "prose", title: "BRRRR Feasibility Snapshot", content: "Purchase: $450k + Rehab: $40k = All-in: $490k\nRefi at 75% ARV ($545k): $409k returned\nCash left in deal: $81k\nVerdict: Partial BRRRR — significant cash trapped." },
  frozen_snapshot: {
    type: "fields", title: "Frozen Assumptions Snapshot", fields: [
      { l: "Purchase Price", v: "$450,000" }, { l: "Rehab Budget", v: "$40,000" }, { l: "ARV", v: "$545,000" },
      { l: "Loan Rate", v: "7.5%" }, { l: "Hold Period", v: "6 months (flip)" }, { l: "Rent Estimate", v: "$2,350/mo" },
    ]
  },
  tiers: {
    type: "tiers", title: "Offer Tiers", tiers: [
      { name: "Tier 3 — Conservative", irr: "≥20% | Strong Flip Margin", price: "$415,000", perUnit: "$146/sqft", color: "green" },
      { name: "Tier 2 — Best & Final", irr: "≥18% | Solid Flip or Hold", price: "$450,000", perUnit: "$158/sqft", color: "navy", rec: true },
      { name: "Tier 1 — Stretch", irr: "≥15% | Thin Margin", price: "$465,000", perUnit: "$163/sqft", color: "amber" },
    ], wt: { price: "$485,000", verdict: "Overpay — negative cash flow, thin flip margin after rehab + carry costs." }
  },
  constraints: { type: "prose", title: "Offer Constraints & Confidence", content: "1. Unpermitted addition risk — verify before hardening offer\n2. Rate sensitivity — hold strategy only viable below 6.5%\n3. Rehab scope creep — foundation/plumbing are unknowns\n4. DOM trending down — some urgency but room to negotiate" },
  recommendation: { type: "prose", title: "Investor Verdict & Recommendation", content: "Anchor at Tier 2 ($450k) — strong flip play with 11% gross margin.\n\nWalk away above $465k unless:\n1. Permits confirmed for addition\n2. Inspection clears major systems\n3. Rehab stays under $45k\n\nFlip preferred over hold at current rates. BRRRR partial — $81k trapped." },
};

/* ═══════════════════════════════════════════════
   CONTACT HEADER
   ═══════════════════════════════════════════════ */
function ContactHeader({ contact }: { contact: Contact }) {
  return (
    <div className="flex items-center gap-5 px-8 py-5 bg-white" style={{ borderBottom: `1px solid ${C.g200}` }}>
      <div className="w-[52px] h-[52px] rounded-xl flex items-center justify-center text-white font-bold text-[16px] flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${C.navy}, ${C.navyLight})` }}>
        {(contact.name || "?").split(" ").map(n => n[0] || "").join("").slice(0, 2) || "?"}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3">
          <h1 className="text-[20px] font-bold truncate" style={{ color: C.g900 }}>{contact.name}</h1>
          <span className="text-[11px] font-mono px-2 py-0.5 rounded-md" style={{ background: C.navyFaint, color: C.navy }}>{contact.id}</span>
        </div>
        <div className="flex items-center gap-4 mt-1 text-[15px]" style={{ color: C.g500 }}>
          <span>{contact.email}</span><span style={{ color: C.g300 }}>·</span><span>{contact.company}</span>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   STEPPER
   ═══════════════════════════════════════════════ */
const STEPS: { label: string; icon: LucideIcon }[] = [
  { label: "Property Type", icon: Building2 }, { label: "Upload Docs", icon: Upload }, { label: "Review Fields", icon: FileCheck },
  { label: "Run Analysis", icon: BarChart3 }, { label: "Report", icon: FileText }, { label: "LOI", icon: FilePlus },
];

function Stepper({ current, completed }: { current: number; completed: number[] }) {
  return (
    <div className="flex items-center gap-0.5 py-5">
      {STEPS.map((step, i) => {
        const Icon = step.icon; const active = i === current; const done = completed.includes(i);
        return (
          <div key={i} className="flex items-center flex-1">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: done ? "#059669" : active ? C.navy : C.g100, color: done || active ? "white" : C.g400, boxShadow: active ? `0 4px 14px ${C.navy}30` : "none" }}>
                {done ? <CheckCircle2 size={18} /> : <Icon size={18} />}
              </div>
              <span className="text-[12px] font-semibold hidden xl:block" style={{ color: active ? C.navy : done ? "#059669" : C.g400 }}>{step.label}</span>
            </div>
            {i < STEPS.length - 1 && <div className="h-px flex-1 mx-3" style={{ background: done ? "#86efac" : C.g200 }} />}
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   STEP 0: PROPERTY TYPE
   ═══════════════════════════════════════════════ */
function PropertyTypeStep({ onSelect }: { onSelect: (t: PropertyType) => void }) {
  const types: { key: PropertyType; icon: LucideIcon; title: string; desc: string; docs: string }[] = [
    ...(SINGLE_FAMILY_ENABLED ? [{ key: "single" as const, icon: Home, title: "Single-Family", desc: "Single-family acquisitions, rehab, rental, and creative finance underwriting", docs: "Photos, comps, listing, inspection, tax records" }] : []),
    { key: "multi", icon: Building2, title: "Multi-Family", desc: "Apartment complexes, portfolio analysis with full financial underwriting", docs: "RR, T12, OM, broker notes, tax/insurance" },
  ];
  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-[26px] font-bold" style={{ color: C.g900 }}>Property Evaluation</h2>
        <p className="text-[16px] mt-1.5" style={{ color: C.g500 }}>Select property type to begin</p>
      </div>
      <div className={types.length === 1 ? "grid grid-cols-1 max-w-md mx-auto gap-4" : "grid grid-cols-1 sm:grid-cols-2 gap-4"}>
        {types.map(t => {
          const Icon = t.icon; return (
            <button key={t.key} onClick={() => onSelect(t.key)} className="group relative bg-white rounded-2xl p-7 text-left transition-all hover:shadow-xl" style={{ border: `2px solid ${C.g200}` }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.navy; }} onMouseLeave={e => (e.currentTarget.style.borderColor = C.g200)}>
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-5" style={{ background: C.navyFaint }}><Icon size={28} style={{ color: C.navy }} /></div>
              <h3 className="text-[18px] font-bold mb-1.5" style={{ color: C.g900 }}>{t.title}</h3>
              <p className="text-[15px] mb-4 leading-relaxed" style={{ color: C.g500 }}>{t.desc}</p>
              <div className="flex items-center gap-2 text-[13px]" style={{ color: C.g400 }}><FileText size={14} />{t.docs}</div>
              <ChevronRight size={20} className="absolute top-7 right-6" style={{ color: C.g300 }} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   STEP 1: UPLOAD — step 2: REVIEW — step 3: ANALYSIS — step 4: REPORT — step 5: LOI
   All follow the same pattern from the JSX but with proper type annotations.
   For brevity in this TSX conversion, the component logic is identical
   to the JSX with types added to all props, state, and event handlers.
   ═══════════════════════════════════════════════ */
const PHOTO_TYPES = [".jpg", ".jpeg", ".png"];
const DOCUMENT_TYPES = [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".csv"];
const ALLOWED_TYPES = [...DOCUMENT_TYPES, ...PHOTO_TYPES];
const MAX_UPLOAD_PHOTOS = 30;
const MAX_UPLOAD_DOCUMENTS = 5;
const MAX_UPLOAD_FILES = MAX_UPLOAD_PHOTOS + MAX_UPLOAD_DOCUMENTS;

function getUploadCategory(name: string): "photo" | "document" | null {
  const ext = "." + name.split(".").pop()?.toLowerCase();
  if (PHOTO_TYPES.includes(ext)) return "photo";
  if (DOCUMENT_TYPES.includes(ext)) return "document";
  return null;
}

function UploadStep({ propertyType, files, setFiles, onNext, onBack }: { propertyType: PropertyType; files: UploadedFile[]; setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>; onNext: (fileIds: string[]) => void; onBack: () => void }) {
  const api = useApi(); const toast = useToast(); const inputRef = useRef<HTMLInputElement>(null); const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const docs = propertyType === "multi" ? MF_DOCS : SF_DOCS;
  const docInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const pendingDocType = useRef<string | null>(null);
  // rawFiles holds the original File objects for upload
  const rawFilesRef = useRef<{ id: number; file: File }[]>([]);
  const add = (list: File[], autoDocType?: string | null) => {
    let currentPhotoCount = 0;
    let currentDocumentCount = 0;
    rawFilesRef.current.forEach(({ file }) => {
      const category = getUploadCategory(file.name);
      if (category === "photo") currentPhotoCount += 1;
      if (category === "document") currentDocumentCount += 1;
    });

    if (currentPhotoCount >= MAX_UPLOAD_PHOTOS && currentDocumentCount >= MAX_UPLOAD_DOCUMENTS) {
      toast(`Upload limit reached. You can upload up to ${MAX_UPLOAD_PHOTOS} photos and ${MAX_UPLOAD_DOCUMENTS} documents per upload.`, "error");
      return;
    }

    const valid: UploadedFile[] = [];
    let blockedPhotos = 0;
    let blockedDocuments = 0;
    for (const f of list) {
      if (f.size > 25 * 1024 * 1024) { toast(`${f.name} exceeds 25 MB`, "error"); continue; }
      const category = getUploadCategory(f.name);
      if (!category) { toast(`${f.name} — unsupported`, "error"); continue; }

      if (category === "photo" && currentPhotoCount >= MAX_UPLOAD_PHOTOS) {
        blockedPhotos += 1;
        continue;
      }
      if (category === "document" && currentDocumentCount >= MAX_UPLOAD_DOCUMENTS) {
        blockedDocuments += 1;
        continue;
      }

      const id = Date.now() + Math.random();
      valid.push({ id, name: f.name, size: f.size, progress: 0, status: "uploading", docType: autoDocType || null, category });
      rawFilesRef.current.push({ id, file: f });
      if (category === "photo") currentPhotoCount += 1;
      if (category === "document") currentDocumentCount += 1;
    }

    if (blockedPhotos || blockedDocuments) {
      const blockedParts: string[] = [];
      if (blockedPhotos) blockedParts.push(`${blockedPhotos} photo${blockedPhotos === 1 ? "" : "s"}`);
      if (blockedDocuments) blockedParts.push(`${blockedDocuments} document${blockedDocuments === 1 ? "" : "s"}`);
      toast(`Upload limits: up to ${MAX_UPLOAD_PHOTOS} photos and ${MAX_UPLOAD_DOCUMENTS} documents per upload. ${blockedParts.join(" and ")} ${blockedParts.length === 1 ? "was" : "were"} not added.`, "warning");
    }

    if (!valid.length) return;
    setFiles(p => [...p, ...valid]);
    valid.forEach(vf => { let p = 0; const iv = setInterval(() => { p += Math.random() * 25 + 15; if (p >= 100) { clearInterval(iv); setFiles(pr => pr.map(f => f.id === vf.id ? { ...f, progress: 100, status: "complete" } : f)); } else { setFiles(pr => pr.map(f => f.id === vf.id ? { ...f, progress: Math.min(p, 99) } : f)); } }, 250); });
  };
  const rm = (id: number) => { setFiles(p => p.filter(f => f.id !== id)); rawFilesRef.current = rawFilesRef.current.filter(r => r.id !== id); };
  const assign = (id: number, t: string | null) => setFiles(p => p.map(f => f.id === id ? { ...f, docType: t } : f));
  const fmt = (b: number) => b < 1048576 ? (b / 1024).toFixed(0) + " KB" : (b / 1048576).toFixed(1) + " MB";
  const hasReqDocs = docs.filter(d => d.req).length > 0;
  const reqMet = hasReqDocs ? docs.filter(d => d.req).every(d => files.some(f => f.docType === d.key && f.status === "complete")) : files.some(f => f.status === "complete") || propertyType === "single";
  const photoCount = files.filter(f => f.category === "photo").length;
  const documentCount = files.filter(f => f.category === "document").length;
  const handleNext = async () => {
    setUploading(true);
    try {
      const rawFiles = rawFilesRef.current.map(r => r.file);
      if (rawFiles.length > 0) {
        const result = await api.uploadFiles(rawFiles);
        onNext(result.files.map(f => f.id));
      } else {
        onNext([]);
      }
    } catch (err) {
      toast(`Upload failed: ${(err as Error).message}`, "error");
      setUploading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6"><h2 className="text-[24px] font-bold" style={{ color: C.g900 }}>Upload {propertyType === "multi" ? "Multi-Family" : "Single-Family"} Documents</h2><p className="text-[16px] mt-1" style={{ color: C.g500 }}>{propertyType === "multi" ? "RR, T12, and OM are required." : "Upload documents or enter details manually below."}</p></div>
      <Card className="p-5 mb-5"><h3 className="text-[12px] font-bold uppercase tracking-widest mb-4" style={{ color: C.g400 }}>Document Checklist</h3><div className={`grid gap-2.5 ${propertyType === "multi" ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2"}`}>{docs.map(d => { const matched = files.some(f => f.docType === d.key && f.status === "complete"); const Ic = d.icon; return (<div key={d.key}><input ref={el => { docInputRefs.current[d.key] = el; }} type="file" multiple accept={ALLOWED_TYPES.join(",")} className="hidden" onChange={e => { const selected = Array.from(e.target.files || []); if (selected.length) add(selected, d.key); e.target.value = ""; }} /><button onClick={() => docInputRefs.current[d.key]?.click()} className="w-full flex items-center gap-2 px-3 py-3 rounded-xl text-[12px] text-left cursor-pointer hover:opacity-80 transition-opacity" style={{ background: matched ? "#ecfdf5" : C.g50, border: `1px solid ${matched ? "#a7f3d0" : C.g200}` }}><Ic size={16} className="flex-shrink-0" style={{ color: matched ? "#059669" : C.g400 }} /><span className="font-medium flex-1 truncate" style={{ color: matched ? "#065f46" : C.g600 }}>{d.label}</span>{d.req && !matched && <span className="text-[10px] font-bold flex-shrink-0" style={{ color: C.red }}>REQ</span>}{matched && <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0" />}</button></div>); })}</div></Card>
      <div className="flex items-start gap-3 rounded-2xl px-4 py-3 mb-4" style={{ background: "#f8fafc", border: `1px solid ${C.g200}` }}>
        <Info size={16} className="mt-0.5 flex-shrink-0" style={{ color: C.g500 }} />
        <div>
          <p className="text-[13px] font-semibold" style={{ color: C.g800 }}>Upload limits</p>
          <p className="text-[13px] leading-relaxed" style={{ color: C.g500 }}>Up to {MAX_UPLOAD_PHOTOS} photos and {MAX_UPLOAD_DOCUMENTS} documents per upload ({MAX_UPLOAD_FILES} files total). Documents include PDF, DOC, DOCX, XLS, XLSX, and CSV files. Max 25 MB per file.</p>
          <p className="text-[12px] mt-1.5" style={{ color: C.g400 }}>{photoCount}/{MAX_UPLOAD_PHOTOS} photos selected · {documentCount}/{MAX_UPLOAD_DOCUMENTS} documents selected</p>
        </div>
      </div>
      <div onClick={() => inputRef.current?.click()} onDragOver={e => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={e => { e.preventDefault(); setDrag(false); add(Array.from(e.dataTransfer.files)); }} className="border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer bg-white" style={{ borderColor: drag ? C.navy : C.g300 }}>
        <input ref={inputRef} type="file" multiple accept={ALLOWED_TYPES.join(",")} className="hidden" onChange={e => add(Array.from(e.target.files || []))} />
        <Upload size={36} style={{ color: drag ? C.navy : C.g400 }} className="mx-auto mb-3" /><p className="text-[16px] font-bold" style={{ color: C.g800 }}>Drop files or click to browse</p><p className="text-[14px] mt-1" style={{ color: C.g400 }}>JPG, JPEG, PNG: up to 30 photos · PDF, DOC, DOCX, XLS, XLSX, CSV: up to 5 documents</p>
      </div>
      {files.length > 0 && <div className="mt-5 space-y-2">{files.map(f => (<div key={f.id} className="flex items-center gap-4 bg-white rounded-xl px-5 py-3.5" style={{ border: `1px solid ${C.g200}` }}><div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: f.status === "complete" ? "#ecfdf5" : C.g50 }}><FileText size={20} style={{ color: f.status === "complete" ? "#059669" : C.g400 }} /></div><div className="flex-1 min-w-0"><div className="flex items-center gap-2.5"><span className="text-[15px] font-semibold truncate" style={{ color: C.g900 }}>{f.name}</span><span className="text-[13px]" style={{ color: C.g400 }}>{fmt(f.size)}</span></div>{f.status === "uploading" && <div className="w-full h-1.5 rounded-full mt-2 overflow-hidden" style={{ background: C.g100 }}><div className="h-full rounded-full transition-all" style={{ width: `${f.progress}%`, background: C.navy }} /></div>}</div>{f.status === "complete" && <select value={f.docType || ""} onChange={e => assign(f.id, e.target.value || null)} className="text-[13px] font-semibold border rounded-xl px-3 py-2.5 bg-white min-w-[170px]" style={{ borderColor: C.g200, color: C.g700 }}><option value="">Assign type…</option>{docs.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}</select>}<button onClick={() => rm(f.id)} className="p-2 rounded-lg hover:bg-red-50"><X size={18} style={{ color: C.g400 }} /></button></div>))}</div>}
      <div className="flex items-center justify-between mt-8"><button onClick={onBack} className="flex items-center gap-2 text-[15px] font-semibold" style={{ color: C.g500 }}><ChevronLeft size={20} /> Back</button><div className="flex items-center gap-3">{propertyType === "single" && <Btn variant="ghost" onClick={() => onNext([])}><ClipboardList size={16} /> Manually Add Property Details</Btn>}<Btn disabled={!reqMet || uploading} onClick={handleNext}>{uploading ? <><Loader2 size={16} className="animate-spin" /> Uploading…</> : <>Continue <ChevronRight size={18} /></>}</Btn></div></div>
    </div>
  );
}


function SFRFieldSections({ fields, setFields, labels }: { fields: Record<string, string>; setFields: React.Dispatch<React.SetStateAction<Record<string, string>>>; labels: Record<string, string> }) {
  const [open, setOpen] = useState<Record<string, boolean>>({ core: true });
  const toggle = (key: string) => setOpen(p => ({ ...p, [key]: !p[key] }));
  const filled = (keys: string[]) => keys.filter(k => (fields[k] || "").trim()).length;

  const sections: { id: string; title: string; keys: string[]; accent?: boolean }[] = [
    { id: "core", title: "1. Core Property Info", keys: ["propertyType", "bedrooms", "bathrooms", "sqft", "lotSize", "yearBuilt", "occupancyStatus", "currentRent", "askingPrice", "sellerAsk", "priceFlexibility"], accent: true },
    { id: "condition", title: "2. Condition / Rehab Info", keys: ["conditionDescription", "rehabEstimate", "contractorEstimate", "inspectionNotes", "roofNotes", "hvacNotes", "foundationNotes", "plumbingNotes", "electricalNotes", "windowNotes", "drainageNotes", "envelopeNotes"] },
    { id: "value", title: "3. Value Support", keys: ["salesComp1", "salesComp2", "salesComp3", "arvEstimate", "rentYieldLogic", "marketContext"] },
    { id: "rent", title: "4. Rent / Income Support", keys: ["currentRentDetail", "leaseStatement", "rentEstimate", "rentBenchmark", "occupancyNotes", "underMarketNotes", "ownerPaidUtilities"] },
    { id: "taxes", title: "5. Taxes / Insurance / Carrying", keys: ["annualTaxes", "annualInsurance", "hoaFee", "hoaDetails", "hoaRules", "mgmtPercent", "maintenancePercent"] },
    { id: "finance", title: "6. Financing Info", keys: ["activeMortgage", "mortgagePayoff", "monthlyMortgagePmt", "pmtIncludesTI", "mortgageCurrent", "openToTerms", "openToSubjectTo", "lenderQuote", "financingAssumptions", "loanType", "rateAssumption", "leverageAssumption", "amortizationIO", "dscrAssumption"] },
    { id: "legal", title: "7. Legal / Record-Risk", keys: ["publicRecordBeds", "publicRecordBaths", "publicRecordSqft", "functionalBeds", "functionalBaths", "functionalSqft", "mismatchPresent", "sellerDisclosure", "legalPermitNotes", "additionsConversions", "liensOwed"] },
    { id: "context", title: "8. Deal Context", keys: ["listingDescription", "sellerNotes", "reasonForSelling", "acquisitionLane", "daysOnMarket", "priceReductions", "competitivePressure", "listedWithRealtor", "listedOnMLS", "propstreamSnapshot", "dealNotes"] },
  ];

  return (
    <>
      {sections.map(s => {
        const isOpen = !!open[s.id];
        const count = filled(s.keys);
        return (
          <Card key={s.id} className="overflow-hidden mb-3">
            <button onClick={() => toggle(s.id)} className="w-full flex items-center gap-3 px-6 py-4 text-left hover:bg-gray-50 transition-colors" style={{ background: s.accent ? (isOpen ? "#fef2f2" : "white") : "white" }}>
              <ChevronRight size={20} style={{ color: s.accent ? "#dc2626" : "#3b82f6", transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 0.2s ease" }} />
              <span className="flex-1 text-[16px] font-semibold" style={{ color: s.accent ? "#dc2626" : "#3b82f6" }}>{s.title}</span>
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: count > 0 ? "#ecfdf5" : C.g100, color: count > 0 ? "#059669" : C.g500 }}>{count} filled</span>
              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: C.g400 }}>Optional</span>
            </button>
            {isOpen && (
              <div style={{ borderTop: `1px solid ${C.g100}` }}>
                {s.keys.map(k => (
                  <div key={k} className="flex items-center gap-5 px-6 py-3.5" style={{ borderTop: `1px solid ${C.g100}` }}>
                    <label className="w-44 text-[12px] font-semibold uppercase tracking-wider flex-shrink-0" style={{ color: C.g400 }}>{labels[k] || k}</label>
                    <input type="text" value={fields[k] || ""} onChange={e => setFields(p => ({ ...p, [k]: e.target.value }))} className="flex-1 text-[15px] font-bold border rounded-xl px-4 py-2.5" style={{ borderColor: C.g200, color: C.g900, background: C.g50 }} placeholder={labels[k] || k} />
                  </div>
                ))}
              </div>
            )}
          </Card>
        );
      })}
    </>
  );
}

function ReviewFieldsStep({ propertyType, fileIds, uploadedFiles, onNext, onBack }: { propertyType: PropertyType; fileIds: string[]; uploadedFiles: UploadedFile[]; onNext: (payload: ReviewFieldsSubmitPayload, standby?: boolean) => void; onBack: () => void }) {
  const api = useApi();
  const toast = useToast();
  const isAppMode = useIsAppMode();
  const [loading, setLoading] = useState(true); const [fields, setFields] = useState<Record<string, string>>({});
  const [rawExtraction, setRawExtraction] = useState<RawExtractionSnapshot | null>(null);
  const [updateCRM, setUpdateCRM] = useState(false);
  const [sfrError, setSfrError] = useState("");
  const uploadedPhotoCount = uploadedFiles.filter(file => file.category === "photo" && file.status === "complete").length;
  const uploadedDocumentCount = uploadedFiles.filter(file => file.category === "document" && file.status === "complete").length;
  const stillUploadingCount = uploadedFiles.filter(file => file.status !== "complete").length;
  const preparedImageCount = rawExtraction?.imageCount ?? 0;
  const handleSFRSubmit = (standby?: boolean) => {
    if (propertyType === "single") {
      const addr = (fields.propertyAddress || "").trim();
      const hasPhotoConditionBasis = uploadedPhotoCount > 0;
      const hasCondition = (fields.conditionDescription || "").trim() || (fields.rehabEstimate || "").trim() || hasPhotoConditionBasis;
      if (!addr) { setSfrError("Property address is required."); return; }
      if (stillUploadingCount > 0) { setSfrError("Photos still uploading. Wait for uploads to finish, then run analysis."); return; }
      if (!hasCondition) { setSfrError("Provide at least one of: condition details, rehab estimate, or property photos."); return; }
      setSfrError("");
    }
    onNext({ fields: { ...fields, updateCRM: updateCRM ? "true" : "false" }, rawExtraction }, standby);
  };
  const labels: Record<string, string> = {
    // MF
    units: "Units", vintage: "Vintage", econOcc: "Economic Occ", avgRent: "Avg Rent", marketRent: "Market Rent", t12NOI: "T12 NOI",
    // Shared
    askingPrice: "Asking Price", propertyAddress: "Property Address", city: "Property City", state: "Property State", zip: "Zip Code",
    // 1. Core Property
    propertyType: "Property Type", bedrooms: "Bedrooms", bathrooms: "Bathrooms", sqft: "Square Feet",
    lotSize: "Lot Size", yearBuilt: "Year Built", occupancyStatus: "Occupancy Status",
    currentRent: "Current Rent", sellerAsk: "Seller Ask", priceFlexibility: "Price Flexibility",
    // 2. Condition / Rehab
    conditionDescription: "Condition / Repairs Needed", rehabEstimate: "Rehab Estimate",
    contractorEstimate: "Contractor Estimate", inspectionNotes: "Inspection Report Notes",
    roofNotes: "Roof", hvacNotes: "HVAC", foundationNotes: "Foundation",
    plumbingNotes: "Plumbing / Sewer", electricalNotes: "Electrical", windowNotes: "Windows",
    drainageNotes: "Drainage / Grading", envelopeNotes: "Exterior Envelope / Siding",
    // 3. Value Support
    arvEstimate: "ARV Estimate", salesComp1: "Sales Comp 1", salesComp2: "Sales Comp 2", salesComp3: "Sales Comp 3",
    rentYieldLogic: "Rent / Yield Logic", marketContext: "Local Market Context",
    // 4. Rent / Income
    currentRentDetail: "Current Rent Details", leaseStatement: "Lease / Rent Statement",
    rentEstimate: "Rent Estimate", rentBenchmark: "Rent Zestimate / Rent Benchmark",
    occupancyNotes: "Occupancy Notes", underMarketNotes: "Under-Market / Weak Lease",
    ownerPaidUtilities: "Owner-Paid Utilities / Fees",
    // 5. Taxes / Insurance / Carrying
    annualTaxes: "Annual Property Taxes", annualInsurance: "Insurance Quote",
    hoaFee: "HOA Fee", hoaDetails: "HOA Details", hoaRules: "HOA Rules",
    mgmtPercent: "Management % Assumption", maintenancePercent: "Maintenance / Reserves %",
    // 6. Financing & Mortgage
    activeMortgage: "Active Mortgage?", mortgagePayoff: "Mortgage Payoff",
    monthlyMortgagePmt: "Monthly Mortgage Payment", pmtIncludesTI: "Pmt Includes Taxes & Insurance?",
    mortgageCurrent: "Mortgage Payments Current?", openToTerms: "Open to Creative Terms",
    openToSubjectTo: "Open to Subject-To",
    lenderQuote: "Lender Quote", financingAssumptions: "Financing Assumptions",
    loanType: "Loan Type", rateAssumption: "Rate Assumption",
    leverageAssumption: "Leverage (LTV / LTC)", amortizationIO: "Amortization / IO",
    dscrAssumption: "DSCR Assumption",
    // 7. Legal / Record-Risk
    publicRecordBeds: "Public Record Beds", publicRecordBaths: "Public Record Baths",
    publicRecordSqft: "Public Record Square Feet",
    functionalBeds: "Functional Beds", functionalBaths: "Functional Baths",
    functionalSqft: "Functional Square Feet",
    mismatchPresent: "Record Mismatch?", sellerDisclosure: "Seller Disclosure Notes",
    legalPermitNotes: "Legal / Permit Notes", additionsConversions: "Additions / Conversions / Non-Permitted",
    liensOwed: "Contractors or Liens Owed",
    // 8. Deal Context
    listingDescription: "Listing Description / Inquiry Notes", sellerNotes: "Seller Notes",
    reasonForSelling: "Reason for Selling",
    acquisitionLane: "Acquisition Lane", daysOnMarket: "Days on Market",
    priceReductions: "Price Reductions", competitivePressure: "Competitive Pressure",
    listedWithRealtor: "Listed with Realtor?", listedOnMLS: "Listed on MLS?",
    propstreamSnapshot: "PropStream Snapshot / Public Data Notes",
    dealNotes: "Deal Notes",
    // Legacy compat
    estARV: "Est. ARV", condition: "Condition",
    publicRecordBedBathSF: "Public Record Beds/Baths/SF", functionalBedBathSF: "Functional Beds/Baths/SF",
  };
  const addressFields = ["propertyAddress", "city", "state", "zip"];
  useEffect(() => {
    if (fileIds.length === 0) {
      // No real files — use demo fallback
      setRawExtraction(null);
      setFields(propertyType === "multi" ? { propertyAddress: "", city: "", state: "", zip: "", units: "", vintage: "", econOcc: "", avgRent: "", marketRent: "", t12NOI: "", askingPrice: "" } : {
        propertyAddress: "", city: "", state: "", zip: "",
        propertyType: "", bedrooms: "", bathrooms: "", sqft: "", lotSize: "", yearBuilt: "", occupancyStatus: "", currentRent: "", askingPrice: "", sellerAsk: "", priceFlexibility: "",
        conditionDescription: "", rehabEstimate: "", contractorEstimate: "", inspectionNotes: "", roofNotes: "", hvacNotes: "", foundationNotes: "", plumbingNotes: "", electricalNotes: "", windowNotes: "", drainageNotes: "", envelopeNotes: "",
        salesComp1: "", salesComp2: "", salesComp3: "", arvEstimate: "", rentYieldLogic: "", marketContext: "",
        currentRentDetail: "", leaseStatement: "", rentEstimate: "", rentBenchmark: "", occupancyNotes: "", underMarketNotes: "", ownerPaidUtilities: "",
        annualTaxes: "", annualInsurance: "", hoaFee: "", hoaDetails: "", hoaRules: "", mgmtPercent: "", maintenancePercent: "",
        activeMortgage: "", mortgagePayoff: "", monthlyMortgagePmt: "", pmtIncludesTI: "", mortgageCurrent: "", openToTerms: "", openToSubjectTo: "", lenderQuote: "", financingAssumptions: "", loanType: "", rateAssumption: "", leverageAssumption: "", amortizationIO: "", dscrAssumption: "",
        publicRecordBeds: "", publicRecordBaths: "", publicRecordSqft: "", functionalBeds: "", functionalBaths: "", functionalSqft: "", mismatchPresent: "", sellerDisclosure: "", legalPermitNotes: "", additionsConversions: "", liensOwed: "",
        listingDescription: "", sellerNotes: "", reasonForSelling: "", acquisitionLane: "", daysOnMarket: "", priceReductions: "", competitivePressure: "", listedWithRealtor: "", listedOnMLS: "", propstreamSnapshot: "", dealNotes: "",
      });
      setLoading(false);
      return;
    }
    api.extractFields({ fileIds, propertyType }).then((result) => {
      const f = { ...result.fields };
      // Ensure address fields exist
      if (!f.city) f.city = "";
      if (!f.state) f.state = "";
      if (!f.zip) f.zip = "";
      if (!f.propertyAddress) f.propertyAddress = "";
      setRawExtraction(result);
      setFields(f); setLoading(false);
    }).catch(err => { toast(`Extraction failed: ${(err as Error).message}`, "error"); setLoading(false); });
  }, []);
  if (loading) return <div className="max-w-2xl mx-auto py-16 text-center"><Loader2 size={40} className="mx-auto mb-4 animate-spin" style={{ color: C.navy }} /><p className="text-[18px] font-bold" style={{ color: C.g800 }}>Extracting data…</p><div className="max-w-md mx-auto mt-8 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div></div>;

  // Separate address fields from other fields
  const otherFields = Object.entries(fields).filter(([k]) => !addressFields.includes(k));

  return (
    <div className="max-w-2xl mx-auto"><div className="mb-6"><h2 className="text-[24px] font-bold" style={{ color: C.g900 }}>Review Extracted Fields</h2></div>

      {propertyType === "single" && (
        <div className="flex items-start gap-3 rounded-2xl px-4 py-3 mb-5" style={{ background: "#f8fafc", border: `1px solid ${C.g200}` }}>
          <Info size={16} className="mt-0.5 flex-shrink-0" style={{ color: C.g500 }} />
          <div>
            <p className="text-[13px] font-semibold" style={{ color: C.g800 }}>
              {uploadedPhotoCount > 0 ? `${uploadedPhotoCount} property photo${uploadedPhotoCount === 1 ? "" : "s"} uploaded` : "No property photos uploaded"}
              {uploadedDocumentCount > 0 ? ` · ${uploadedDocumentCount} document${uploadedDocumentCount === 1 ? "" : "s"} uploaded` : ""}
            </p>
            <p className="text-[13px] leading-relaxed mt-1" style={{ color: C.g500 }}>
              {preparedImageCount > 0 ? `${preparedImageCount} image${preparedImageCount === 1 ? "" : "s"} prepared for condition analysis. ` : ""}
              Leave optional fields blank when you want the analysis to estimate from photos, documents, and assumptions.
            </p>
          </div>
        </div>
      )}

      {/* Address Section */}
      <Card className="overflow-hidden mb-5">
        <div className="px-6 py-3" style={{ background: C.g50, borderBottom: `1px solid ${C.g100}` }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <p className="text-[12px] font-bold uppercase tracking-widest" style={{ color: C.g400 }}>Property Address</p>
              {propertyType === "single" && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: "#fef2f2", color: C.red }}>Required</span>}
            </div>
            {!isAppMode && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={updateCRM} onChange={e => setUpdateCRM(e.target.checked)} className="w-4 h-4 rounded" style={{ accentColor: C.navy }} />
                <span className="text-[12px] font-semibold" style={{ color: C.g500 }}>Update CRM Address</span>
              </label>
            )}
          </div>
        </div>
        <div className="px-6 py-4 space-y-3">
          <div className="flex items-center gap-5">
            <label className="w-44 text-[13px] font-semibold uppercase tracking-wider" style={{ color: C.g400 }}>Address</label>
            <input type="text" value={fields.propertyAddress || ""} onChange={e => setFields(p => ({ ...p, propertyAddress: e.target.value }))} className="flex-1 text-[16px] font-bold border rounded-xl px-4 py-3" style={{ borderColor: C.g200, color: C.g900, background: C.g50 }} placeholder="Street address" />
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-5 flex-1">
              <label className="w-44 text-[13px] font-semibold uppercase tracking-wider flex-shrink-0" style={{ color: C.g400 }}>City</label>
              <input type="text" value={fields.city || ""} onChange={e => setFields(p => ({ ...p, city: e.target.value }))} className="flex-1 text-[16px] font-bold border rounded-xl px-4 py-3" style={{ borderColor: C.g200, color: C.g900, background: C.g50 }} placeholder="City" />
            </div>
            <div className="flex items-center gap-2" style={{ width: "140px" }}>
              <input type="text" value={fields.state || ""} onChange={e => setFields(p => ({ ...p, state: e.target.value }))} className="w-full text-[16px] font-bold border rounded-xl px-4 py-3" style={{ borderColor: C.g200, color: C.g900, background: C.g50 }} placeholder="State" />
            </div>
            <div className="flex items-center gap-2" style={{ width: "140px" }}>
              <input type="text" value={fields.zip || ""} onChange={e => setFields(p => ({ ...p, zip: e.target.value }))} className="w-full text-[16px] font-bold border rounded-xl px-4 py-3" style={{ borderColor: C.g200, color: C.g900, background: C.g50 }} placeholder="Zip" />
            </div>
          </div>
        </div>
      </Card>

      {/* Other Fields — sectioned for SFR */}
      {propertyType === "single" ? <SFRFieldSections fields={fields} setFields={setFields} labels={labels} /> : <Card className="divide-y overflow-hidden mb-5">{otherFields.map(([k, v]) => (<div key={k} className="flex items-center gap-5 px-6 py-4" style={{ borderColor: C.g100 }}><label className="w-44 text-[13px] font-semibold uppercase tracking-wider" style={{ color: C.g400 }}>{labels[k] || k}</label><input type="text" value={v} onChange={e => setFields(p => ({ ...p, [k]: e.target.value }))} className="flex-1 text-[16px] font-bold border rounded-xl px-4 py-3" style={{ borderColor: C.g200, color: C.g900, background: C.g50 }} /></div>))}</Card>}
      {sfrError && <div className="mt-4 px-4 py-3 rounded-xl text-[14px] font-semibold flex items-center gap-2" style={{ background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" }}><AlertTriangle size={16} /> {sfrError}</div>}
      <div className="flex items-center justify-between mt-8">
        <button onClick={onBack} className="flex items-center gap-2 text-[15px] font-semibold" style={{ color: C.g500 }}><ChevronLeft size={20} /> Back</button>
        <div className="flex items-center gap-3">
          <Btn variant="ghost" onClick={() => handleSFRSubmit(true)} className="border-dashed" style={{ borderColor: C.g300 }}>
            Stand By (Manual)
          </Btn>
          <Btn onClick={() => handleSFRSubmit(false)}>Run Analysis <ArrowRight size={18} /></Btn>
        </div>
      </div>
    </div>
  );
}

function AnalysisStep({ propertyType, contactId, fields, fileIds, rawExtraction, standby, onComplete }: { propertyType: PropertyType; contactId: string; fields: Record<string, string>; fileIds: string[]; rawExtraction: RawExtractionSnapshot | null; standby?: boolean; onComplete: (reportId: string) => void }) {
  const api = useApi();
  const [progress, setProgress] = useState(0); const [stage, setStage] = useState(standby ? "Preparing report..." : "System initializing…"); const [status, setStatus] = useState<"running" | "complete" | "failed">("running");
  const [log, setLog] = useState<{ label: string; time: string }[]>([]);
  const [errorMsg, setErrorMsg] = useState("");
  const toast = useToast();
  useEffect(() => {
    let cancelled = false;
    let pollTimer: ReturnType<typeof setInterval> | undefined;

    const pollJob = async (jobId: string) => {
      if (cancelled) return;
      try {
        const job = await api.pollAnalysis(jobId);
        if (cancelled) return;
        setProgress(job.progress); setStage(job.stage);
        setLog(p => { const last = p[p.length - 1]; return last?.label === job.stage ? p : [...p, { label: job.stage, time: new Date().toLocaleTimeString() }]; });
        if (job.status === "complete" && job.reportId) {
          if (pollTimer) clearInterval(pollTimer);
          setStatus("complete");
          toast("System process complete");
          setTimeout(() => {
            if (!cancelled) onComplete(job.reportId!);
          }, 1200);
        }
        else if (job.status === "failed") {
          if (pollTimer) clearInterval(pollTimer);
          setStatus("failed");
          setErrorMsg(job.error || "System process failed");
          toast("System process failed", "error");
        }
      } catch {
        /* retry next tick */
      }
    };

    const analysisBody: AnalysisStartBody = {
      contactId,
      propertyType,
      fields,
      fileIds,
      standby,
      rawExtractedFields: rawExtraction?.fields,
      rawExtractedText: rawExtraction?.extractedText,
      rawExtractedImageCount: rawExtraction?.imageCount,
      rawExtractionDurationMs: rawExtraction?.durationMs,
    };

    api.startAnalysis(analysisBody).then(({ jobId }) => {
      if (cancelled) return;
      setStage(standby ? "Generating report..." : "System analysis started…"); setProgress(10);
      setLog(p => [...p, { label: standby ? "System report generation started" : "System analysis started", time: new Date().toLocaleTimeString() }]);
      void pollJob(jobId);
      pollTimer = setInterval(() => { void pollJob(jobId); }, 2000);
    }).catch(err => {
      if (cancelled) return;
      setStatus("failed"); setErrorMsg((err as Error).message); toast("Failed to start system process", "error");
    });
    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
    };
  }, []);
  if (status === "failed") return <div className="max-w-xl mx-auto py-6 text-center"><div className="w-24 h-24 rounded-3xl mx-auto mb-5 flex items-center justify-center" style={{ background: "#fef2f2" }}><AlertCircle size={40} style={{ color: C.red }} /></div><h2 className="text-[24px] font-bold mb-2" style={{ color: C.g900 }}>Analysis Failed</h2><p className="text-[15px]" style={{ color: C.red }}>{errorMsg}</p></div>;
  return (
    <div className="max-w-xl mx-auto py-6 text-center">
      <div className="w-24 h-24 rounded-3xl mx-auto mb-5 flex items-center justify-center" style={{ background: status === "complete" ? "#ecfdf5" : C.navyFaint }}>{status === "running" ? <Loader2 size={40} className="animate-spin" style={{ color: C.navy }} /> : <CheckCircle2 size={40} className="text-emerald-600" />}</div>
      <h2 className="text-[24px] font-bold" style={{ color: C.g900 }}>{status === "complete" ? "Complete" : "System Processing"}</h2>
      <p className="text-[16px] mt-1 mb-6" style={{ color: C.g500 }}>{stage}</p>
      <div className="w-full h-3.5 rounded-full overflow-hidden mb-8" style={{ background: C.g100 }}><div className="h-full rounded-full transition-all duration-700" style={{ width: `${progress}%`, background: status === "complete" ? "#059669" : C.navy }} /></div>
      <Card className="p-5 max-h-52 overflow-y-auto text-left"><div className="space-y-2">{log.map((s, i) => (<div key={i} className="flex items-center gap-3 text-[14px]"><CheckCircle2 size={16} className="text-emerald-500" /><span className="flex-1" style={{ color: C.g600 }}>{s.label}</span><span className="font-mono text-[12px]" style={{ color: C.g400 }}>{s.time}</span></div>))}</div></Card>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   CUSTOM FIELD MAPPER
   ═══════════════════════════════════════════════ */
interface FieldMapping {
  crmKey: string;
  crmLabel: string;
  value: string;
  ghlFieldId: string | null;
  ghlFieldKey: string | null;
  ghlFieldName: string | null;
}

function CustomFieldMapper({ contactId, crmFields, onSaved }: {
  contactId: string;
  crmFields: CRMField[];
  onSaved: () => void;
}) {
  const api = useApi(); const toast = useToast();
  const [ghlFields, setGhlFields] = useState<GHLCustomField[]>([]);
  const [loadingFields, setLoadingFields] = useState(true);
  const [mappings, setMappings] = useState<FieldMapping[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
  const [creatingFor, setCreatingFor] = useState<string | null>(null);
  const [newFieldName, setNewFieldName] = useState("");
  const [newFieldType, setNewFieldType] = useState<string>("TEXT");
  const [createLoading, setCreateLoading] = useState(false);
  const [refreshingFields, setRefreshingFields] = useState(false);

  useEffect(() => {
    const baseMappings = crmFields.map(f => ({
      crmKey: f.key, crmLabel: f.label, value: f.val,
      ghlFieldId: null as string | null, ghlFieldKey: null as string | null, ghlFieldName: null as string | null,
    }));
    // Load saved mappings for this location and apply them
    api.getFieldMappings().then(({ mappings: saved }) => {
      const updated = baseMappings.map(m => {
        const s = saved.find(sv => sv.crmKey === m.crmKey);
        if (s) return { ...m, ghlFieldId: s.ghlFieldId, ghlFieldKey: s.ghlFieldKey, ghlFieldName: s.ghlFieldName };
        return m;
      });
      setMappings(updated);
    }).catch(() => {
      setMappings(baseMappings);
    });
    setSaved(false);
  }, [crmFields]);

  const loadGHLFields = (showFeedback = false) => {
    if (showFeedback) setRefreshingFields(true);
    else setLoadingFields(true);
    api.getCustomFields()
      .then(r => {
        setGhlFields(r.customFields);
        if (showFeedback) {
          toast(`Loaded ${r.customFields.length} custom field${r.customFields.length === 1 ? "" : "s"}`, "success");
        }
        setLoadingFields(false);
      })
      .catch((e) => {
        toast(`Failed to load custom fields: ${(e as Error).message}`, "error");
        setLoadingFields(false);
      })
      .finally(() => setRefreshingFields(false));
  };

  useEffect(() => {
    loadGHLFields();
  }, []);

  const updateMapping = (crmKey: string, field: GHLCustomField | null) => {
    setMappings(m => m.map(x => x.crmKey === crmKey
      ? { ...x, ghlFieldId: field?.id ?? null, ghlFieldKey: field?.fieldKey ?? null, ghlFieldName: field?.name ?? null }
      : x
    ));
    setSaved(false);
    setOpenDropdown(null);
    setSearchTerms(p => ({ ...p, [crmKey]: "" }));
  };

  const handleCreateField = async (crmKey: string) => {
    if (!newFieldName.trim()) return;
    setCreateLoading(true);
    try {
      const { customField } = await api.createCustomField({ name: newFieldName.trim(), dataType: newFieldType });
      setGhlFields(p => [...p, customField]);
      updateMapping(crmKey, customField);
      setCreatingFor(null);
      setNewFieldName("");
      setNewFieldType("TEXT");
      toast(`Custom field "${customField.name}" created`, "success");
    } catch (e) {
      toast(`Failed to create field: ${(e as Error).message}`, "error");
    } finally {
      setCreateLoading(false);
    }
  };

  const handleSave = async () => {
    const mapped = mappings.filter(m => m.ghlFieldId);
    if (!mapped.length) { toast("Map at least one field before saving", "error"); return; }
    setSaving(true);
    try {
      // Save field values to GHL contact
      await api.saveCustomFieldValues(contactId, mapped.map(m => ({
        id: m.ghlFieldId!,
        key: m.ghlFieldKey || undefined,
        field_value: m.value,
      })));
      // Persist mappings for this location so they're remembered next time
      await api.saveFieldMappings(mapped.map(m => ({
        crmKey: m.crmKey,
        ghlFieldId: m.ghlFieldId!,
        ghlFieldKey: m.ghlFieldKey || undefined,
        ghlFieldName: m.ghlFieldName || undefined,
      }))).catch(() => { /* non-fatal */ });
      setSaved(true);
      toast(`${mapped.length} custom field${mapped.length > 1 ? "s" : ""} saved to contact`, "success");
      onSaved();
    } catch (e) {
      toast(`Save failed: ${(e as Error).message}`, "error");
    } finally {
      setSaving(false);
    }
  };

  const DATA_TYPES = [
    { value: "TEXT", label: "Text" }, { value: "LARGE_TEXT", label: "Long Text" },
    { value: "NUMERICAL", label: "Number" }, { value: "MONETARY", label: "Monetary" },
    { value: "DATE", label: "Date" }, { value: "EMAIL", label: "Email" },
    { value: "PHONE", label: "Phone" }, { value: "URL", label: "URL" },
    { value: "CHECKBOX", label: "Checkbox" }, { value: "DROPDOWN", label: "Dropdown" },
  ];

  const mappedCount = mappings.filter(m => m.ghlFieldId).length;

  return (
    <Card className="p-5 mt-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${C.navy}12` }}>
            <Link2 size={18} style={{ color: C.navy }} />
          </div>
          <div>
            <h3 className="text-[15px] font-bold" style={{ color: C.g900 }}>Map to GHL Custom Fields</h3>
            <p className="text-[12px] mt-0.5" style={{ color: C.g500 }}>Link system fields to contact custom fields, or create new ones</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!loadingFields && (
            <button onClick={() => loadGHLFields(true)} disabled={refreshingFields} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-60" title="Refresh fields">
              {refreshingFields ? <Loader2 size={15} className="animate-spin" style={{ color: C.navy }} /> : <RefreshCw size={15} style={{ color: C.g400 }} />}
            </button>
          )}
          <button onClick={handleSave} disabled={saving || saved || mappedCount === 0}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-bold text-white"
            style={{ background: saved ? "#059669" : mappedCount === 0 ? C.g300 : C.navy, cursor: mappedCount === 0 ? "not-allowed" : "pointer" }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <CheckCircle2 size={14} /> : <Save size={14} />}
            {saved ? "Saved" : saving ? "Saving…" : `Save ${mappedCount > 0 ? `(${mappedCount})` : ""}`}
          </button>
        </div>
      </div>

      {loadingFields ? (
        <div className="flex items-center justify-center py-8 gap-3">
          <Loader2 size={18} className="animate-spin" style={{ color: C.navy }} />
          <span className="text-[14px]" style={{ color: C.g500 }}>Loading custom fields…</span>
        </div>
      ) : (
        <div className="space-y-2">
          {mappings.map(mapping => {
            const search = searchTerms[mapping.crmKey] || "";
            const filtered = ghlFields.filter(f =>
              f.name.toLowerCase().includes(search.toLowerCase()) ||
              f.fieldKey.toLowerCase().includes(search.toLowerCase())
            );
            const isOpen = openDropdown === mapping.crmKey;
            const isCreating = creatingFor === mapping.crmKey;

            return (
              <div key={mapping.crmKey} className="rounded-xl overflow-visible" style={{ border: `1px solid ${C.g200}`, background: C.g50 }}>
                <div className="px-4 py-3 space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: C.g400 }}>{mapping.crmLabel}</p>
                      <p className="text-[14px] font-bold mt-0.5 truncate" style={{ color: C.g900 }}>{mapping.value}</p>
                    </div>

                    <ArrowRight size={16} style={{ color: C.g300, flexShrink: 0 }} />
                  </div>

                  <div className="relative">
                    <button
                      onClick={() => { setOpenDropdown(isOpen ? null : mapping.crmKey); setCreatingFor(null); }}
                      className="w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-left text-[13px] font-semibold"
                      style={{
                        border: `1.5px solid ${isOpen ? C.navy : mapping.ghlFieldId ? "#a7f3d0" : C.g200}`,
                        background: mapping.ghlFieldId ? "#ecfdf5" : "white",
                        color: mapping.ghlFieldId ? "#065f46" : C.g500,
                      }}>
                      {mapping.ghlFieldId ? <CheckCircle2 size={15} className="text-emerald-600 flex-shrink-0" /> : <Tag size={15} style={{ color: C.g400, flexShrink: 0 }} />}
                      <span className="flex-1 truncate">{mapping.ghlFieldName || "Select custom field…"}</span>
                      <ChevronDown size={15} style={{ color: C.g400, flexShrink: 0 }} />
                    </button>

                    {isOpen && !isCreating && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-xl z-50 overflow-hidden"
                        style={{ border: `1.5px solid ${C.navy}`, maxHeight: 280 }}>
                        <div className="p-2.5 border-b" style={{ borderColor: C.g100 }}>
                          <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg" style={{ background: C.g50, border: `1px solid ${C.g200}` }}>
                            <Search size={14} style={{ color: C.g400 }} />
                            <input autoFocus type="text" value={search}
                              onChange={e => setSearchTerms(p => ({ ...p, [mapping.crmKey]: e.target.value }))}
                              placeholder="Search custom fields…"
                              className="flex-1 bg-transparent text-[13px] outline-none font-medium"
                              style={{ color: C.g900 }} />
                          </div>
                        </div>

                        <div className="overflow-y-auto" style={{ maxHeight: 180 }}>
                          {mapping.ghlFieldId && (
                            <button onClick={() => updateMapping(mapping.crmKey, null)}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-red-50"
                              style={{ color: C.red }}>
                              <X size={14} /> Clear mapping
                            </button>
                          )}
                          {filtered.length === 0 && !search && (
                            <p className="px-4 py-3 text-[13px]" style={{ color: C.g400 }}>No custom fields found. Create one below.</p>
                          )}
                          {filtered.length === 0 && search && (
                            <p className="px-4 py-3 text-[13px]" style={{ color: C.g400 }}>No fields match "{search}"</p>
                          )}
                          {filtered.map(field => (
                            <button key={field.id} onClick={() => updateMapping(mapping.crmKey, field)}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50"
                              style={{ background: mapping.ghlFieldId === field.id ? C.navyFaint : undefined }}>
                              <div className="flex-1 min-w-0">
                                <p className="text-[13px] font-bold truncate" style={{ color: C.g900 }}>{field.name}</p>
                                <p className="text-[11px] font-mono truncate" style={{ color: C.g400 }}>{field.fieldKey}</p>
                              </div>
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded flex-shrink-0" style={{ background: C.g100, color: C.g500 }}>{field.dataType}</span>
                              {mapping.ghlFieldId === field.id && <CheckCircle2 size={14} className="text-emerald-600 flex-shrink-0" />}
                            </button>
                          ))}
                        </div>

                        <div className="border-t p-2.5" style={{ borderColor: C.g100 }}>
                          <button onClick={() => { setCreatingFor(mapping.crmKey); setNewFieldName(search || mapping.crmLabel); setOpenDropdown(null); }}
                            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] font-bold hover:bg-gray-50"
                            style={{ color: C.navy, border: `1.5px dashed ${C.g200}` }}>
                            <Plus size={15} /> Create new field{search ? ` "${search}"` : ""}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {isCreating && (
                  <div className="mx-4 mb-4 p-4 rounded-xl" style={{ background: C.navyFaint, border: `1.5px solid ${C.navy}30` }}>
                    <p className="text-[12px] font-bold uppercase tracking-wider mb-3" style={{ color: C.navy }}>Create Custom Field</p>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-[11px] font-bold mb-1" style={{ color: C.g600 }}>Field Name</label>
                        <input autoFocus type="text" value={newFieldName}
                          onChange={e => setNewFieldName(e.target.value)}
                          placeholder="e.g. T12 NOI"
                          className="w-full px-3 py-2 rounded-lg text-[13px] font-semibold outline-none"
                          style={{ border: `1.5px solid ${C.g200}`, background: "white", color: C.g900 }}
                          onKeyDown={e => { if (e.key === "Enter") handleCreateField(mapping.crmKey); if (e.key === "Escape") setCreatingFor(null); }}
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold mb-1" style={{ color: C.g600 }}>Data Type</label>
                        <select value={newFieldType} onChange={e => setNewFieldType(e.target.value)}
                          className="w-full px-3 py-2 rounded-lg text-[13px] font-semibold outline-none appearance-none"
                          style={{ border: `1.5px solid ${C.g200}`, background: "white", color: C.g900 }}>
                          {DATA_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleCreateField(mapping.crmKey)} disabled={createLoading || !newFieldName.trim()}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-bold text-white"
                        style={{ background: C.navy, opacity: (!newFieldName.trim() || createLoading) ? 0.5 : 1 }}>
                        {createLoading ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                        {createLoading ? "Creating…" : "Create Field"}
                      </button>
                      <button onClick={() => setCreatingFor(null)}
                        className="px-4 py-2 rounded-lg text-[13px] font-semibold"
                        style={{ color: C.g500, background: "white", border: `1px solid ${C.g200}` }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loadingFields && mappedCount > 0 && !saved && (
        <div className="mt-4 flex items-center gap-2.5 px-4 py-3 rounded-xl" style={{ background: "#ecfdf5", border: "1px solid #a7f3d0" }}>
          <CheckCircle2 size={16} className="text-emerald-600" />
          <p className="text-[13px] font-bold" style={{ color: "#065f46" }}>{mappedCount} of {mappings.length} fields mapped. Click Save to write to contact.</p>
        </div>
      )}
      {!loadingFields && ghlFields.length === 0 && (
        <div className="mt-3 flex items-center gap-2.5 px-4 py-3 rounded-xl" style={{ background: "#fffbeb", border: "1px solid #fde68a" }}>
          <AlertTriangle size={16} style={{ color: "#d97706" }} />
          <p className="text-[13px] font-medium" style={{ color: "#92400e" }}>No custom fields found for this location. Use "Create new field" to add them.</p>
        </div>
      )}
    </Card>
  );
}

function asTraceRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function formatTraceBlock(value: unknown): string {
  if (value === undefined || value === null || value === "") return "None";
  if (typeof value === "string") return value;
  return JSON.stringify(value, null, 2);
}

function asStringList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(v => (typeof v === "string" ? v : JSON.stringify(v))).filter(Boolean);
  return [];
}

function TracePre({ value }: { value: unknown }) {
  return <pre className="rounded-xl p-4 text-[12px] leading-relaxed whitespace-pre-wrap break-words overflow-x-auto" style={{ background: C.g50, color: C.g700, border: `1px solid ${C.g100}` }}>{formatTraceBlock(value)}</pre>;
}

function TraceSection({ icon: Icon, title, subtitle, children, defaultOpen = true }: { icon: LucideIcon; title: string; subtitle?: string; children: ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${C.g200}` }}>
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-3 px-5 py-3.5 text-left" style={{ background: C.g50 }}>
        <span className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "white", color: C.navy, border: `1px solid ${C.g200}` }}><Icon size={16} strokeWidth={1.9} /></span>
        <span className="flex-1 min-w-0">
          <span className="block text-[14px] font-bold" style={{ color: C.g900 }}>{title}</span>
          {subtitle && <span className="block text-[12px] mt-0.5" style={{ color: C.g500 }}>{subtitle}</span>}
        </span>
        {open ? <ChevronUp size={16} style={{ color: C.g400 }} /> : <ChevronDown size={16} style={{ color: C.g400 }} />}
      </button>
      {open && <div className="px-5 py-4" style={{ borderTop: `1px solid ${C.g100}` }}>{children}</div>}
    </section>
  );
}

function AnalysisTraceModal({ trace, loading, error, onClose }: { trace: AnalysisTrace | null; loading: boolean; error: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const t = asTraceRecord(trace);
  const doc = asTraceRecord(t.documentText);
  const images = asTraceRecord(t.images);
  const calculations = Array.isArray(t.backendCalculations) ? t.backendCalculations as Array<Record<string, unknown>> : [];
  const sourceFiles = asStringList(t.sourceFiles);
  const imageLabels = asStringList(images.labelsSent);
  const provenance = Array.isArray(t.dataProvenance) ? t.dataProvenance as Array<Record<string, unknown>> : [];

  const copyJson = async () => {
    try { await navigator.clipboard.writeText(JSON.stringify(trace, null, 2)); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch { /* clipboard blocked */ }
  };
  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(trace, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `analysis-log-${String(t.reportId || "report")}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const stats: [string, string][] = [
    ["Prompt Profile", String(t.promptProfile || "—")],
    ["Model", String(t.model || "—")],
    ["Generated", t.generatedAt ? new Date(String(t.generatedAt)).toLocaleString() : "—"],
    ["Document Text Sent", `${Number(doc.sentChars || 0).toLocaleString()} / ${Number(doc.originalChars || 0).toLocaleString()} chars`],
    ["Photos Sent To Vision", `${Number(images.sent || 0)} / ${Number(images.available || 0)}`],
    ["Self-Correction Retry", t.placeholderCorrectionRetried ? "Yes" : "No"],
  ];

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 sm:p-6" style={{ background: "rgba(15,23,36,0.6)" }}>
      <div className="w-full max-w-5xl max-h-[90vh] flex flex-col rounded-2xl bg-white shadow-2xl overflow-hidden" style={{ border: `1px solid ${C.g200}` }}>
        {/* Header */}
        <div className="flex items-center justify-between gap-4 px-6 py-4 flex-shrink-0" style={{ background: C.navy }}>
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(255,255,255,0.14)" }}><Shield size={20} color="white" /></span>
            <div className="min-w-0">
              <h2 className="text-[19px] font-bold text-white">Internal Analysis Log</h2>
              <p className="text-[12.5px] truncate" style={{ color: "rgba(255,255,255,0.72)" }}>Exactly what the AI used to evaluate this property — sources, inputs, formulas &amp; assumptions.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {trace && !loading && !error && (
              <>
                <button onClick={copyJson} className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12.5px] font-semibold" style={{ background: "rgba(255,255,255,0.14)", color: "white" }}>{copied ? <><CheckCircle2 size={14} /> Copied</> : <><FileText size={14} /> Copy JSON</>}</button>
                <button onClick={downloadJson} className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12.5px] font-semibold" style={{ background: "rgba(255,255,255,0.14)", color: "white" }}><Download size={14} /> Export</button>
              </>
            )}
            <button onClick={onClose} className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "rgba(255,255,255,0.14)", color: "white" }}><X size={18} /></button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-6 space-y-4">
          {loading ? (
            <div className="py-20 text-center"><Loader2 size={30} className="mx-auto animate-spin" style={{ color: C.navy }} /><p className="text-[13px] mt-3" style={{ color: C.g500 }}>Loading analysis trace…</p></div>
          ) : error ? (
            <div className="rounded-xl p-4 text-[14px] font-semibold" style={{ color: C.red, background: C.redFaint, border: "1px solid #fca5a5" }}>{error}</div>
          ) : (
            <>
              {t.message !== undefined && (
                <div className="rounded-xl p-4 text-[13px] font-semibold leading-relaxed" style={{ background: C.navyFaint, color: C.navy, border: `1px solid ${C.g200}` }}>{String(t.message)}</div>
              )}

              {/* KEY CALLOUT — data provenance (the accuracy question) */}
              <div className="rounded-2xl p-5" style={{ background: "#fffbeb", border: "1px solid #fde68a" }}>
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} style={{ color: "#b45309", marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <p className="text-[14px] font-bold mb-1" style={{ color: "#92400e" }}>Where the numbers come from</p>
                    <p className="text-[13px] leading-relaxed" style={{ color: "#92400e" }}>{String(t.externalDataPolicy || "No external-data policy was captured for this run.")}</p>
                  </div>
                </div>
              </div>

              {/* DATA SOURCES, GAPS & CALCULATIONS — per-field provenance */}
              {provenance.length > 0 && (
                <TraceSection icon={ClipboardList} title="Data Sources, Gaps & Calculations" subtitle={`Per-input breakdown — where each number came from, why, and how it was calculated (${provenance.length} items)`}>
                  <div className="space-y-2.5">
                    {provenance.map((p, i) => {
                      const status = String(p.status || "").toLowerCase();
                      const sc = status === "verified" ? { bg: "#ecfdf5", bd: "#a7f3d0", fg: "#065f46", label: "Verified" }
                        : status === "estimated" ? { bg: "#fffbeb", bd: "#fde68a", fg: "#92400e", label: "Estimated" }
                        : status === "missing" ? { bg: C.redFaint, bd: "#fca5a5", fg: C.red, label: "Missing" }
                        : { bg: C.g50, bd: C.g200, fg: C.g600, label: status || "—" };
                      return (
                        <div key={i} className="rounded-xl p-4" style={{ border: `1px solid ${C.g200}` }}>
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-0">
                              <p className="text-[14px] font-bold" style={{ color: C.g900 }}>{String(p.item || `Item ${i + 1}`)}</p>
                              {p.value !== undefined && p.value !== "" && <p className="text-[15px] font-bold mt-0.5" style={{ color: C.navy }}>{String(p.value)}</p>}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {p.source !== undefined && <span className="px-2.5 py-1 rounded-lg text-[11.5px] font-semibold" style={{ background: C.navyFaint, color: C.navy }}>{String(p.source)}</span>}
                              <span className="px-2.5 py-1 rounded-lg text-[11.5px] font-bold" style={{ background: sc.bg, color: sc.fg, border: `1px solid ${sc.bd}` }}>{sc.label}</span>
                            </div>
                          </div>
                          {p.basis !== undefined && p.basis !== "" && (
                            <div className="mt-2.5">
                              <p className="text-[10.5px] font-bold uppercase tracking-widest" style={{ color: C.g400 }}>Where / why</p>
                              <p className="text-[13px] leading-relaxed mt-0.5" style={{ color: C.g700 }}>{String(p.basis)}</p>
                            </div>
                          )}
                          {p.calculation !== undefined && p.calculation !== "" && (
                            <div className="mt-2.5">
                              <p className="text-[10.5px] font-bold uppercase tracking-widest" style={{ color: C.g400 }}>How it was calculated</p>
                              <p className="text-[12.5px] leading-relaxed mt-1 font-mono rounded-lg px-3 py-2 break-words" style={{ color: C.g800, background: C.g50 }}>{String(p.calculation)}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </TraceSection>
              )}

              {/* Summary stats */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {stats.map(([label, value]) => (
                  <div key={label} className="rounded-xl p-3.5" style={{ background: C.g50, border: `1px solid ${C.g100}` }}>
                    <p className="text-[10.5px] font-bold uppercase tracking-widest" style={{ color: C.g400 }}>{label}</p>
                    <p className="text-[13.5px] font-bold mt-1 break-words" style={{ color: C.g900 }}>{value}</p>
                  </div>
                ))}
              </div>

              <TraceSection icon={FileText} title="Source Documents" subtitle={`${sourceFiles.length} file(s) the analysis was built from`}>
                {sourceFiles.length ? (
                  <ul className="space-y-1.5">{sourceFiles.map((f, i) => <li key={i} className="flex items-center gap-2 text-[13px] font-semibold" style={{ color: C.g700 }}><FileCheck size={14} style={{ color: C.navy, flexShrink: 0 }} />{f}</li>)}</ul>
                ) : <p className="text-[13px]" style={{ color: C.g500 }}>No source files recorded.</p>}
              </TraceSection>

              <TraceSection icon={Eye} title="Photos Sent To AI Vision" subtitle={`${Number(images.sent || 0)} of ${Number(images.available || 0)} uploaded image(s) analyzed`}>
                {imageLabels.length ? (
                  <div className="flex flex-wrap gap-2">{imageLabels.map((l, i) => <span key={i} className="px-2.5 py-1 rounded-lg text-[12px] font-semibold" style={{ background: C.navyFaint, color: C.navy }}>{l}</span>)}</div>
                ) : <p className="text-[13px]" style={{ color: C.g500 }}>No photos were sent to vision for this run.</p>}
              </TraceSection>

              <TraceSection icon={BarChart3} title="Backend Formulas & Heuristics" subtitle="Deterministic math the server applied (not the AI)">
                {calculations.length === 0 ? (
                  <p className="text-[13px]" style={{ color: C.g500 }}>No backend calculations captured.</p>
                ) : (
                  <div className="space-y-3">
                    {calculations.map((calc, idx) => (
                      <div key={idx} className="rounded-xl p-4" style={{ border: `1px solid ${C.g200}` }}>
                        <p className="text-[14px] font-bold" style={{ color: C.g900 }}>{String(calc.name || `Calculation ${idx + 1}`)}</p>
                        {calc.formula !== undefined && <p className="text-[12.5px] mt-1.5 font-mono rounded-lg px-3 py-2 break-words" style={{ color: C.navy, background: C.navyFaint }}>{String(calc.formula)}</p>}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
                          <div><p className="text-[10.5px] font-bold uppercase tracking-widest mb-1" style={{ color: C.g400 }}>Inputs</p><TracePre value={calc.inputs} /></div>
                          <div><p className="text-[10.5px] font-bold uppercase tracking-widest mb-1" style={{ color: C.g400 }}>Output</p><TracePre value={calc.output} /></div>
                        </div>
                        {calc.notes !== undefined && <div className="mt-3"><p className="text-[10.5px] font-bold uppercase tracking-widest mb-1" style={{ color: C.g400 }}>Notes</p><TracePre value={calc.notes} /></div>}
                      </div>
                    ))}
                  </div>
                )}
              </TraceSection>

              <TraceSection icon={BookOpen} title="AI's Own Audit Log" subtitle="The model's stated sources, comps, formulas & assumptions">
                <TracePre value={t.modelAnalysisLog} />
              </TraceSection>

              {t.finalTiers !== undefined && (
                <TraceSection icon={DollarSign} title="Final Tier Pricing">
                  <TracePre value={t.finalTiers} />
                </TraceSection>
              )}

              <TraceSection icon={Target} title="Investor Criteria Applied" defaultOpen={false}>
                <TracePre value={t.investorCriteria} />
              </TraceSection>

              {t.packageDerivedHints !== undefined && (
                <TraceSection icon={Info} title="Package-Derived Hints" defaultOpen={false}>
                  <TracePre value={t.packageDerivedHints} />
                </TraceSection>
              )}

              <TraceSection icon={ClipboardList} title="Extracted / Reviewed Fields" defaultOpen={false}>
                <TracePre value={t.extractedFields} />
              </TraceSection>

              <TraceSection icon={FileText} title="Full Document Text Sent To Model" defaultOpen={false} subtitle={`${Number(doc.sentChars || 0).toLocaleString()} characters`}>
                <TracePre value={doc.sentToModel} />
              </TraceSection>

              <TraceSection icon={Search} title="Raw Trace JSON" defaultOpen={false}>
                <pre className="rounded-xl p-4 text-[12px] whitespace-pre-wrap break-words overflow-x-auto" style={{ background: C.g900, color: "white" }}>{formatTraceBlock(trace)}</pre>
              </TraceSection>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ReportStep({ propertyType, reportId, contactId, onGoToLOI }: { propertyType: PropertyType; reportId: string | null; contactId: string; onGoToLOI: () => void }) {
  const api = useApi(); const toast = useToast(); const isAppMode = useIsAppMode();
  const [reportData, setReportData] = useState<Record<string, ReportSectionData> | null>(null);
  const [crmFields, setCrmFields] = useState<CRMField[]>([]);
  const [legacyReport, setLegacyReport] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const sections = propertyType === "multi" ? MF_SECTIONS : SF_SECTIONS;
  const [active, setActive] = useState(sections[0].key);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<{ section: string; idx: number } | null>(null);
  const [draftSections, setDraftSections] = useState<Record<string, ReportSectionData>>({});
  const [savingSectionKey, setSavingSectionKey] = useState<string | null>(null);
  const [sectionSaveState, setSectionSaveState] = useState<Record<string, "idle" | "saving" | "saved" | "error">>({});
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [loadingAutoSave, setLoadingAutoSave] = useState(false);
  const [savingReportAgain, setSavingReportAgain] = useState(false);
  const [reportSaveAgainState, setReportSaveAgainState] = useState<"idle" | "success" | "error">("idle");
  const [, setCrmSaved] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [traceOpen, setTraceOpen] = useState(false);
  const [traceLoading, setTraceLoading] = useState(false);
  const [analysisTrace, setAnalysisTrace] = useState<AnalysisTrace | null>(null);
  const [traceError, setTraceError] = useState("");
  const autosaveTimers = useRef<Record<string, ReturnType<typeof setTimeout> | undefined>>({});
  const autosaveRequestIds = useRef<Record<string, number>>({});

  const cloneSection = useCallback((section: ReportSectionData): ReportSectionData => ({
    ...section,
    fields: section.fields?.map(field => ({ ...field })),
    tiers: section.tiers?.map(tier => ({ ...tier })),
    wt: section.wt ? { ...section.wt } : undefined,
  }), []);

  useEffect(() => {
    setActive(sections[0].key);
    setEditingSection(null);
    setEditingField(null);
    setDraftSections({});
    setSectionSaveState({});
  }, [sections, reportId]);

  useEffect(() => () => {
    Object.values(autosaveTimers.current).forEach(timer => {
      if (timer) clearTimeout(timer);
    });
  }, []);

  useEffect(() => {
    if (reportId) {
      setLoadError("");
      api.getReport(reportId).then(({ report }) => {
        setReportData(report.sections as Record<string, ReportSectionData>);
        setCrmFields((report.crmFields || []).map(f => ({ key: f.key, label: f.label, val: f.val })));
        setLegacyReport(!!report.legacySource);
      }).catch((err) => {
        // Never silently substitute sample data for a real report — surface the error instead.
        setReportData(null);
        setLegacyReport(false);
        setLoadError((err as Error)?.message || "We couldn't load this report.");
      });
    } else {
      setReportData(propertyType === "multi" ? MF_REPORT : SF_REPORT);
      setCrmFields(propertyType === "multi" ? CRM_FIELDS_MF : CRM_FIELDS_SF);
      setLegacyReport(false);
    }
  }, [api, propertyType, reportId, reloadKey]);

  useEffect(() => {
    if (isAppMode) return;
    let cancelled = false;
    setLoadingAutoSave(true);
    api.getSettings().then(({ settings }) => {
      if (!cancelled) setAutoSaveEnabled(settings.autoSaveReport !== false);
    }).catch(() => {
      if (!cancelled) setAutoSaveEnabled(true);
    }).finally(() => {
      if (!cancelled) setLoadingAutoSave(false);
    });
    return () => { cancelled = true; };
  }, [api, isAppMode]);

  const report = reportData || (propertyType === "multi" ? MF_REPORT : SF_REPORT);
  const sectionMeta = sections.find(section => section.key === active) ?? sections[0];
  const fallbackActiveSection: ReportSectionData = {
    type: "prose",
    title: sectionMeta.label,
    content: legacyReport
      ? "This section is not available for the restored legacy PDF. Use Download PDF to view the historical report that was uploaded to GoHighLevel."
      : "Source details are available for newly generated reports. Re-run analysis to capture photo, document, and assumption provenance.",
  };
  const data = report[active] ?? fallbackActiveSection;
  const activeDraft = editingSection === active ? draftSections[active] ?? data : data;
  const ActiveSectionIcon = sectionMeta.icon;

  const startEditingSection = (sectionKey: string) => {
    const section = report[sectionKey];
    if (!section) return;
    setDraftSections(prev => ({ ...prev, [sectionKey]: cloneSection(section) }));
    setEditingSection(sectionKey);
  };

  const startEditingField = (sectionKey: string, idx: number) => {
    const section = report[sectionKey];
    if (!section) return;
    setDraftSections(prev => ({ ...prev, [sectionKey]: cloneSection(prev[sectionKey] ?? section) }));
    setEditingSection(sectionKey);
    setEditingField({ section: sectionKey, idx });
  };

  const updateDraftSection = (sectionKey: string, updater: (section: ReportSectionData) => ReportSectionData) => {
    const source = cloneSection(draftSections[sectionKey] ?? report[sectionKey]);
    if (!source) return;
    const nextSection = updater(source);

    setDraftSections(prev => ({ ...prev, [sectionKey]: nextSection }));
    setReportData(prev => ({
      ...(prev ?? (propertyType === "multi" ? MF_REPORT : SF_REPORT)),
      [sectionKey]: nextSection,
    }));
    setSectionSaveState(prev => ({ ...prev, [sectionKey]: "idle" }));

    const requestId = (autosaveRequestIds.current[sectionKey] ?? 0) + 1;
    autosaveRequestIds.current[sectionKey] = requestId;

    if (autosaveTimers.current[sectionKey]) {
      clearTimeout(autosaveTimers.current[sectionKey]);
    }

    autosaveTimers.current[sectionKey] = setTimeout(() => {
      setSavingSectionKey(sectionKey);
      setSectionSaveState(prev => ({ ...prev, [sectionKey]: "saving" }));

      const handleCompletion = (state: "saved" | "error") => {
        if (autosaveRequestIds.current[sectionKey] !== requestId) return;
        setSavingSectionKey(current => current === sectionKey ? null : current);
        setSectionSaveState(prev => ({ ...prev, [sectionKey]: state }));
        if (state === "saved") {
          setTimeout(() => {
            setSectionSaveState(prev => prev[sectionKey] === "saved" ? { ...prev, [sectionKey]: "idle" } : prev);
          }, 1800);
        }
      };

      if (!reportId) {
        handleCompletion("saved");
        return;
      }

      api.updateReportSection(reportId, sectionKey, nextSection as unknown as Record<string, unknown>).then(() => {
        handleCompletion("saved");
      }).catch(() => {
        handleCompletion("error");
      });
    }, 650);
  };

  const handleDownloadPDF = async () => {
    if (!reportId) return;
    setDownloading(true);
    try {
      const blob = await api.downloadReport(reportId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "Analysis Report.pdf"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast(`Download failed: ${(e as Error).message}`, "error"); }
    finally { setDownloading(false); }
  };

  const handleSaveReportAgain = async () => {
    if (!reportId) { toast("No report to save", "error"); return; }
    setSavingReportAgain(true);
    setReportSaveAgainState("idle");
    try {
      const result = await api.saveReportToDocs(reportId);
      if (result.success === false) {
        setReportSaveAgainState("error");
        toast(result.warning || "Report generated, but CRM upload is unavailable.", "error");
        return;
      }
      setReportSaveAgainState("success");
      toast("Report saved again to Extra", "success");
    } catch (e) {
      setReportSaveAgainState("error");
      toast(`Save failed: ${(e as Error).message}`, "error");
    } finally {
      setSavingReportAgain(false);
    }
  };

  const handleOpenTrace = async () => {
    if (!reportId) return;
    setTraceOpen(true);
    setTraceLoading(true);
    setTraceError("");
    try {
      const result = await api.getAnalysisTrace(reportId);
      setAnalysisTrace(result.trace);
    } catch (e) {
      setTraceError((e as Error).message);
    } finally {
      setTraceLoading(false);
    }
  };

  if (loadError && reportId) return (
    <div className="max-w-lg mx-auto py-16 text-center">
      <div className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{ background: C.redFaint }}><AlertCircle size={32} style={{ color: C.red }} /></div>
      <h2 className="text-[20px] font-bold mb-2" style={{ color: C.g900 }}>Couldn&apos;t load this report</h2>
      <p className="text-[14px] mb-6" style={{ color: C.g500 }}>{loadError}</p>
      <button onClick={() => { setLoadError(""); setReloadKey(k => k + 1); }} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-[14px] font-bold text-white" style={{ background: C.navy }}><RefreshCw size={16} /> Retry</button>
    </div>
  );
  if (!reportData && reportId) return <div className="py-16 text-center"><Loader2 size={36} className="mx-auto animate-spin" style={{ color: C.navy }} /></div>;
  if (!data || !activeDraft) return null;

  const isEditingActiveSection = editingSection === active;
  const textInputClass = "w-full rounded-xl px-3 py-2.5 text-[15px] font-semibold outline-none";
  const textAreaClass = "w-full text-[16px] leading-relaxed rounded-xl px-5 py-4 outline-none resize-y";

  const tierCardStyles = (tier: TierData): { background: string; border: string; heading: string } => {
    if (tier.rec) return { background: C.navyFaint, border: C.navy, heading: C.navy };
    if (tier.color === "green") return { background: "#f0fdf4", border: "#bbf7d0", heading: "#166534" };
    return { background: "#fffbeb", border: "#fde68a", heading: C.g900 };
  };

  const renderFieldSectionEditor = (section: ReportSectionData) => (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
      {(section.fields || []).map((field, idx) => {
        const isEditingRow = editingField?.section === active && editingField?.idx === idx;
        return (
          <div key={idx} className="relative rounded-xl p-4" style={{ background: C.g50, border: `1px solid ${isEditingRow ? C.navy : C.g100}` }}>
            <button
              onClick={() => startEditingField(active, idx)}
              className="absolute top-3 right-3 inline-flex items-center justify-center w-7 h-7 rounded-lg transition-opacity"
              style={{ background: "transparent", color: C.g300, border: `1px solid transparent`, opacity: isEditingRow ? 1 : 0.7 }}
            >
              <Pencil size={12} strokeWidth={1.7} />
            </button>
            <p className="text-[11px] font-semibold uppercase tracking-wider pr-8" style={{ color: C.g400 }}>{field.l}</p>
            {isEditingRow ? (
              <input
                autoFocus
                value={field.v}
                onChange={e => updateDraftSection(active, current => ({
                  ...current,
                  fields: (current.fields || []).map((item, itemIdx) => itemIdx === idx ? { ...item, v: e.target.value } : item),
                }))}
                onBlur={() => setEditingField(null)}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    e.currentTarget.blur();
                  }
                  if (e.key === "Escape") {
                    setEditingField(null);
                  }
                }}
                className={textInputClass}
                style={{ border: `1px solid ${C.g200}`, color: C.g900, background: "white", marginTop: 6 }}
              />
            ) : (
              <p className="text-[16px] font-bold mt-1 pr-8" style={{ color: C.g900 }}>{field.v}</p>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderProseSectionEditor = (section: ReportSectionData) => (
    <div className="space-y-4">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: C.g400 }}>Section Content</p>
        <textarea
          value={section.content || ""}
          onChange={e => updateDraftSection(active, current => ({ ...current, content: e.target.value }))}
          rows={14}
          className={textAreaClass}
          style={{ border: `1.5px solid ${C.navy}`, color: C.g700, background: C.g50 }}
        />
      </div>
    </div>
  );

  const renderTierSectionEditor = (section: ReportSectionData) => (
    <div className="space-y-4">
      <div className="space-y-3">
        {(section.tiers || []).map((tier, idx) => {
          const styles = tierCardStyles(tier);
          return (
            <div key={idx} className="rounded-2xl p-5" style={{ background: styles.background, border: `1.5px solid ${styles.border}` }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: C.g400 }}>Tier Name</p>
                  <input
                    value={tier.name}
                    onChange={e => updateDraftSection(active, current => ({
                      ...current,
                      tiers: (current.tiers || []).map((item, itemIdx) => itemIdx === idx ? { ...item, name: e.target.value } : item),
                    }))}
                    className={textInputClass}
                    style={{ border: `1px solid ${C.g200}`, color: styles.heading, background: "white" }}
                  />
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: C.g400 }}>IRR / Posture</p>
                  <input
                    value={tier.irr}
                    onChange={e => updateDraftSection(active, current => ({
                      ...current,
                      tiers: (current.tiers || []).map((item, itemIdx) => itemIdx === idx ? { ...item, irr: e.target.value } : item),
                    }))}
                    className={textInputClass}
                    style={{ border: `1px solid ${C.g200}`, color: C.g700, background: "white" }}
                  />
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: C.g400 }}>Price</p>
                  <input
                    value={tier.price}
                    onChange={e => updateDraftSection(active, current => ({
                      ...current,
                      tiers: (current.tiers || []).map((item, itemIdx) => itemIdx === idx ? { ...item, price: e.target.value } : item),
                    }))}
                    className={textInputClass}
                    style={{ border: `1px solid ${C.g200}`, color: C.g900, background: "white" }}
                  />
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: C.g400 }}>Per Unit</p>
                  <input
                    value={tier.perUnit}
                    onChange={e => updateDraftSection(active, current => ({
                      ...current,
                      tiers: (current.tiers || []).map((item, itemIdx) => itemIdx === idx ? { ...item, perUnit: e.target.value } : item),
                    }))}
                    className={textInputClass}
                    style={{ border: `1px solid ${C.g200}`, color: C.g700, background: "white" }}
                  />
                </div>
              </div>
              <label className="inline-flex items-center gap-2 mt-4 text-[13px] font-bold" style={{ color: C.g700 }}>
                <input
                  type="checkbox"
                  checked={!!tier.rec}
                  onChange={e => updateDraftSection(active, current => ({
                    ...current,
                    tiers: (current.tiers || []).map((item, itemIdx) => {
                      if (itemIdx === idx) return { ...item, rec: e.target.checked };
                      return e.target.checked ? { ...item, rec: false } : item;
                    }),
                  }))}
                />
                Recommended tier
              </label>
            </div>
          );
        })}
      </div>
      {section.wt && (
        <div className="rounded-2xl p-4" style={{ background: C.redFaint, border: "1px solid #fca5a5" }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: "#991b1b" }}>Walk-Away Price</p>
              <input
                value={section.wt.price}
                onChange={e => updateDraftSection(active, current => ({ ...current, wt: { ...(current.wt || { price: "", verdict: "" }), price: e.target.value } }))}
                className={textInputClass}
                style={{ border: `1px solid #fca5a5`, color: "#7f1d1d", background: "white" }}
              />
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: "#991b1b" }}>Verdict</p>
              <input
                value={section.wt.verdict}
                onChange={e => updateDraftSection(active, current => ({ ...current, wt: { ...(current.wt || { price: "", verdict: "" }), verdict: e.target.value } }))}
                className={textInputClass}
                style={{ border: `1px solid #fca5a5`, color: "#7f1d1d", background: "white" }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderReadOnlyTiers = (section: ReportSectionData) => (
    <div className="space-y-3">
      {(section.tiers || []).map((tier, idx) => {
        const styles = tierCardStyles(tier);
        return (
          <div key={idx} className="rounded-2xl p-5" style={{ background: styles.background, border: `2px solid ${styles.border}` }}>
            <div className="flex items-center justify-between gap-4">
              <div>
                <h4 className="text-[16px] font-bold" style={{ color: C.g900 }}>{tier.name}</h4>
                <p className="text-[14px]" style={{ color: C.g500 }}>{tier.irr}</p>
              </div>
              <div className="text-right">
                <p className="text-[24px] font-bold" style={{ color: tier.rec ? C.navy : C.g900 }}>{tier.price}</p>
                <p className="text-[14px]" style={{ color: C.g500 }}>{tier.perUnit}</p>
              </div>
            </div>
            {tier.rec && <Badge variant="info" className="mt-2">RECOMMENDED</Badge>}
          </div>
        );
      })}
      {section.wt && (
        <div className="rounded-xl p-4" style={{ background: C.redFaint, border: "1px solid #fca5a5" }}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={18} style={{ color: C.red }} />
            <span className="text-[15px] font-bold" style={{ color: "#7f1d1d" }}>Walk-Away — {section.wt.price}</span>
          </div>
          <p className="text-[14px]" style={{ color: "#991b1b" }}>{section.wt.verdict}</p>
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    if (isEditingActiveSection) {
      if (activeDraft.type === "fields") return renderFieldSectionEditor(activeDraft);
      if (activeDraft.type === "tiers") return renderTierSectionEditor(activeDraft);
      return renderProseSectionEditor(activeDraft);
    }

    if (data.type === "fields") return renderFieldSectionEditor(data);
    if (data.type === "tiers") return renderReadOnlyTiers(data);
    return <div className="text-[16px] leading-relaxed whitespace-pre-line" style={{ color: C.g700 }}>{data.content}</div>;
  };

  const activeSaveState = sectionSaveState[active] || "idle";

  return (
    <div className="flex gap-6">
      {traceOpen && <AnalysisTraceModal trace={analysisTrace} loading={traceLoading} error={traceError} onClose={() => setTraceOpen(false)} />}
      <div className="w-56 flex-shrink-0">
        <h3 className="text-[12px] font-bold uppercase tracking-widest px-2 mb-3" style={{ color: C.g400 }}>Sections</h3>
        <nav className="space-y-1 mb-5">
          {sections.map(section => {
            const Icon = section.icon;
            const isActive = active === section.key;
            return (
              <button
                key={section.key}
                onClick={() => setActive(section.key)}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-[14px] font-bold text-left"
                style={{ background: isActive ? C.navy : "transparent", color: isActive ? "white" : C.g600 }}
              >
                <span
                  className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: isActive ? "rgba(255,255,255,0.16)" : C.g50, color: isActive ? "rgba(255,255,255,0.92)" : C.g400 }}
                >
                  <Icon size={14} strokeWidth={1.7} />
                </span>
                <span>{section.label}</span>
              </button>
            );
          })}
        </nav>
        {SHOW_ANALYSIS_TRACE && (
          <button onClick={handleOpenTrace} disabled={!reportId || traceLoading} className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-[13px] font-bold mb-3" style={{ background: C.g900, color: "white", opacity: !reportId || traceLoading ? 0.65 : 1 }}>
            {traceLoading ? <><Loader2 size={15} className="animate-spin" /> Loading…</> : <><ClipboardList size={15} /> Analysis Log</>}
          </button>
        )}
        <button onClick={handleDownloadPDF} disabled={downloading || !reportId} className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-[13px] font-bold mb-3" style={{ background: "white", color: C.navy, border: `1.5px solid ${C.g200}` }}>{downloading ? <><Loader2 size={15} className="animate-spin" /> Generating…</> : <><Download size={15} /> Download PDF</>}</button>
        <button onClick={onGoToLOI} disabled={legacyReport} title={legacyReport ? "Legacy PDFs need a new analysis before generating an LOI" : undefined} className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-[13px] font-bold text-white mb-4" style={{ background: C.navy, opacity: legacyReport ? 0.55 : 1, cursor: legacyReport ? "not-allowed" : "pointer" }}><FilePlus size={15} /> Generate LOI</button>
        {!isAppMode && (
          <Card className="p-4 mb-4">
            <h4 className="text-[11px] font-bold uppercase tracking-widest mb-3" style={{ color: C.g400 }}>
              <Save size={14} className="inline mr-1.5" style={{ verticalAlign: "-2px" }} />
              Auto-Save
            </h4>
            {loadingAutoSave ? (
              <div className="flex items-center gap-2 text-[13px] font-semibold" style={{ color: C.g500 }}>
                <Loader2 size={15} className="animate-spin" />
                Loading settings…
              </div>
            ) : autoSaveEnabled ? (
              <div className="rounded-xl p-3.5" style={{ background: "#ecfdf5", border: "1px solid #a7f3d0" }}>
                <div className="flex items-center gap-2 text-[13px] font-bold" style={{ color: "#065f46" }}>
                  <CheckCircle2 size={15} />
                  Reports auto-save to contact documents
                </div>
                <p className="text-[12px] mt-1.5" style={{ color: "#047857" }}>Completed reports are saved automatically under Extra.</p>
                <button
                  onClick={handleSaveReportAgain}
                  disabled={!reportId || savingReportAgain}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-[13px] font-bold mt-3"
                  style={{
                    background: reportSaveAgainState === "success" ? "#059669" : reportSaveAgainState === "error" ? C.red : C.navy,
                    color: "white",
                    opacity: !reportId || savingReportAgain ? 0.7 : 1,
                  }}
                >
                  {savingReportAgain ? <><Loader2 size={15} className="animate-spin" /> Saving…</> : reportSaveAgainState === "success" ? <><CheckCircle2 size={15} /> Saved Again</> : reportSaveAgainState === "error" ? <><AlertCircle size={15} /> Save Failed</> : <><Save size={15} /> Save Again</>}
                </button>
              </div>
            ) : (
              <div className="rounded-xl p-3.5" style={{ background: "#fffbeb", border: "1px solid #fde68a" }}>
                <div className="flex items-center gap-2 text-[13px] font-bold" style={{ color: "#92400e" }}>
                  <AlertTriangle size={15} />
                  Auto-save is off
                </div>
                <p className="text-[12px] mt-1.5" style={{ color: "#a16207" }}>Turn on Auto-Save in Settings to send reports to contact documents.</p>
              </div>
            )}
          </Card>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-4 mb-5">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: C.g50, border: `1px solid ${C.g200}`, color: C.g400 }}>
              <ActiveSectionIcon size={16} strokeWidth={1.7} />
            </div>
            <div className="min-w-0">
              <h2 className="text-[22px] font-bold truncate" style={{ color: C.g900 }}>{activeDraft.title}</h2>
              {isEditingActiveSection && activeSaveState === "saving" && <p className="text-[12px] font-semibold mt-1" style={{ color: C.g400 }}>Saving changes…</p>}
              {isEditingActiveSection && activeSaveState === "saved" && <p className="text-[12px] font-semibold mt-1" style={{ color: "#059669" }}>Saved</p>}
              {isEditingActiveSection && activeSaveState === "error" && <p className="text-[12px] font-semibold mt-1" style={{ color: C.red }}>Auto-save failed</p>}
            </div>
          </div>
        </div>
        <div className="relative rounded-2xl p-5" style={{ background: "white", border: `1px solid ${C.g200}` }}>
          {data.type !== "fields" && (
            <button
              onClick={() => isEditingActiveSection ? setEditingSection(null) : startEditingSection(active)}
              className="absolute top-4 right-4 inline-flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12px] font-bold"
              style={{ background: C.g50, color: isEditingActiveSection ? C.red : C.g600, border: `1px solid ${isEditingActiveSection ? "#fca5a5" : C.g200}` }}
            >
              {isEditingActiveSection && savingSectionKey === active ? <Loader2 size={13} className="animate-spin" /> : <Pencil size={13} strokeWidth={1.8} />}
              {isEditingActiveSection ? "Done" : "Edit"}
            </button>
          )}
          {renderContent()}
        </div>
        {active === sections[0].key && (
          <CustomFieldMapper
            contactId={contactId}
            crmFields={crmFields}
            onSaved={() => setCrmSaved(true)}
          />
        )}
      </div>
    </div>
  );
}

function LOIStep({ propertyType, reportId, contactId, onBack }: { propertyType: PropertyType; reportId: string | null; contactId: string; onBack: () => void }) {
  const api = useApi(); const toast = useToast();
  const [templates, setTemplates] = useState<LOITemplate[]>([]);
  const [loadingTpl, setLoadingTpl] = useState(true);
  const [tpl, setTpl] = useState("");
  const [mode, setMode] = useState<"template" | "ai">("template");
  const [gen, setGen] = useState(false); const [loiId, setLoiId] = useState<string | null>(null); const [downloadUrl, setDownloadUrl] = useState("");
  const [saveLOI, setSaveLOI] = useState(false); const [savingLOI, setSavingLOI] = useState(false);
  const [emailSent, setEmailSent] = useState(false); const [sendingEmail, setSendingEmail] = useState(false);
  const [c1, setC1] = useState(true); const [c2, setC2] = useState(true); const [c3, setC3] = useState(true);
  const [c1Text, setC1Text] = useState("");
  const [c2Text, setC2Text] = useState("");
  const [c3Text, setC3Text] = useState("");
  const [showEditor, setShowEditor] = useState(false);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<LOITemplate | null>(null);
  const [deletingTplId, setDeletingTplId] = useState<string | null>(null);
  const [tiers, setTiers] = useState<TierData[]>([]);
  const [selectedTier, setSelectedTier] = useState("");

  // Load tiers from report
  useEffect(() => {
    if (!reportId) return;
    api.getReport(reportId).then(({ report }) => {
      const sections = report.sections as Record<string, ReportSectionData>;
      const tiersSection = sections.tiers;
      if (tiersSection?.tiers) {
        setTiers(tiersSection.tiers);
        if (!selectedTier && tiersSection.tiers.length > 0) {
          const rec = tiersSection.tiers.find(t => t.rec);
          setSelectedTier(rec?.name || tiersSection.tiers[0].name);
        }
      }
    }).catch(() => { });
  }, [reportId]);

  const loadTemplates = () => {
    setLoadingTpl(true);
    api.getTemplates().then(r => {
      const filtered = r.templates.filter(t => t.propertyType === propertyType);
      setTemplates(filtered);
      if (!tpl && filtered.length > 0) setTpl(filtered[0].id);
      setLoadingTpl(false);
    }).catch(() => setLoadingTpl(false));
  };
  useEffect(loadTemplates, [propertyType]);

  // Load LOI default texts from location settings
  useEffect(() => {
    api.getSettings().then(r => {
      const s = r.settings;
      if (s.loiContingenciesText) setC1Text(prev => prev || s.loiContingenciesText!);
      if (s.loiEarnestText) setC2Text(prev => prev || s.loiEarnestText!);
      if (s.loiTimelineText) setC3Text(prev => prev || s.loiTimelineText!);
    }).catch(() => {});
  }, []);

  const doGen = async () => {
    if (!reportId) { toast("No report available for LOI generation", "error"); return; }
    if (!selectedTier) { toast("Select an offer tier", "error"); return; }
    setGen(true);
    try {
      const selectedTplObj = templates.find(t => t.id === tpl);
      const genMode = selectedTplObj?.isSystem ? "ai" : mode;
      const res = await api.generateLOI({ contactId, reportId, templateId: tpl, tier: selectedTier, mode: genMode, options: { contingencies: c1, earnest: c2, timeline: c3, contingenciesText: c1Text, earnestText: c2Text, timelineText: c3Text } });
      setLoiId(res.loiId); setDownloadUrl(res.downloadUrl); toast("LOI generated & saved to documents");
    } catch (e) { toast(`LOI generation failed: ${(e as Error).message}`, "error"); }
    finally { setGen(false); }
  };
  const handleSaveLOI = async () => {
    if (!loiId) return;
    setSavingLOI(true);
    try {
      const result = await api.saveLOI(loiId);
      if (result.success === false) {
        toast(result.warning || "LOI generated, but CRM upload is unavailable.", "error");
        return;
      }
      setSaveLOI(true);
      toast("LOI saved to documents");
    } catch (e) { toast(`Save failed: ${(e as Error).message}`, "error"); }
    finally { setSavingLOI(false); }
  };
  const handleSendEmail = async () => {
    if (!loiId) return;
    setSendingEmail(true);
    try { await api.sendEmail({ contactId, loiId }); setEmailSent(true); toast("Email sent via CRM"); }
    catch (e) { toast(`Email failed: ${(e as Error).message}`, "error"); }
    finally { setSendingEmail(false); }
  };
  const handleDeleteTemplate = async (id: string) => {
    setDeletingTplId(id);
    try { await api.deleteTemplate(id); toast("Template deleted"); loadTemplates(); if (tpl === id) setTpl(""); } catch (e) { toast(`Delete failed: ${(e as Error).message}`, "error"); }
    finally { setDeletingTplId(null); }
  };

  const selectedTpl = templates.find(t => t.id === tpl);
  const selectedTierObj = tiers.find(t => t.name === selectedTier);
  const tierBg = (t: TierData) => t.rec ? C.navyFaint : t.color === "green" ? "#f0fdf4" : "#fffbeb";
  const tierBorder = (t: TierData) => t.rec ? C.navy : t.color === "green" ? "#bbf7d0" : "#fde68a";
  const tierAccent = (t: TierData) => t.rec ? C.navy : t.color === "green" ? "#059669" : "#d97706";
  const optionColors = ["#059669", "#2563eb", "#d97706"];

  return (
    <div className="max-w-2xl mx-auto">
      {showEditor && loiId && (
        <Suspense fallback={null}>
          <LOIEditor loiId={loiId} onClose={() => setShowEditor(false)} onSaved={() => toast("LOI updated & PDF regenerated")} />
        </Suspense>
      )}
      {showTemplateEditor && (
        <Suspense fallback={<div style={{ position: "fixed", inset: 0, background: "rgba(15,23,36,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}><Loader2 size={32} className="animate-spin" style={{ color: "white" }} /></div>}>
          <LOITemplateEditor
            template={editingTemplate}
            onSave={() => { setShowTemplateEditor(false); setEditingTemplate(null); loadTemplates(); toast("Template saved"); }}
            onClose={() => { setShowTemplateEditor(false); setEditingTemplate(null); }}
          />
        </Suspense>
      )}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-[26px] font-bold" style={{ color: C.g900 }}>Generate LOI</h2>
          <p className="text-[14px] mt-1" style={{ color: C.g500 }}>Configure your Letter of Intent and generate a PDF</p>
        </div>
        <button onClick={() => { setEditingTemplate(null); setShowTemplateEditor(true); }} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-bold" style={{ background: C.navyFaint, color: C.navy, border: `1.5px solid ${C.g200}`, cursor: "pointer" }}>
          <FilePlus size={15} /> New Template
        </button>
      </div>

      {/* Generated LOI — Success state (shown at top when generated) */}
      {loiId && (
        <Card className="p-0 mb-6 overflow-hidden" style={{ border: "2px solid #a7f3d0" }}>
          <div className="px-6 py-5 text-center" style={{ background: "#ecfdf5" }}>
            <CheckCircle2 size={40} className="text-emerald-600 mx-auto mb-3" />
            <p className="text-[20px] font-bold" style={{ color: "#065f46" }}>LOI Generated Successfully</p>
            <div className="inline-flex items-center gap-2 mt-2 px-4 py-2 rounded-xl text-[13px]" style={{ background: "white", border: `1px solid ${C.g200}` }}>
              <FolderOpen size={14} style={{ color: C.navy }} />
              <span style={{ color: C.g600 }}>Auto-saved to:</span>
              <span className="font-bold" style={{ color: C.navy }}>Contact Documents → LOI</span>
            </div>
          </div>
          <div className="p-6" style={{ background: "white" }}>
            <button onClick={() => setShowEditor(true)}
              className="w-full flex items-center gap-4 p-5 rounded-2xl text-left mb-4 hover:opacity-90 transition-opacity"
              style={{ background: `linear-gradient(135deg, ${C.navy}, ${C.navyLight})`, cursor: "pointer" }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(255,255,255,0.2)" }}>
                <FileText size={22} className="text-white" />
              </div>
              <div>
                <p className="text-[16px] font-bold text-white">Open LOI Editor</p>
                <p className="text-[13px] text-white/70">Customize text, clauses, and formatting. PDF auto-regenerates on save.</p>
              </div>
              <ChevronRight size={20} className="text-white/60 ml-auto flex-shrink-0" />
            </button>
            <div className="grid grid-cols-3 gap-3">
              <button onClick={() => window.open(downloadUrl, "_blank")}
                className="flex items-center justify-center gap-2 p-3.5 rounded-xl text-[13px] font-bold hover:bg-gray-50 transition-colors"
                style={{ border: `1.5px solid ${C.g200}`, color: C.g700, background: "white", cursor: "pointer" }}>
                <Download size={16} /> Download PDF
              </button>
              <button onClick={handleSaveLOI} disabled={saveLOI || savingLOI}
                className="flex items-center justify-center gap-2 p-3.5 rounded-xl text-[13px] font-bold hover:bg-gray-50 transition-colors"
                style={{ border: `1.5px solid ${saveLOI ? "#a7f3d0" : C.g200}`, color: saveLOI ? "#059669" : C.g700, background: saveLOI ? "#ecfdf5" : "white", cursor: "pointer", opacity: savingLOI ? 0.7 : 1 }}>
                {savingLOI ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : saveLOI ? <><CheckCircle2 size={16} /> Saved</> : <><Save size={16} /> Save to Docs</>}
              </button>
              <button onClick={handleSendEmail} disabled={emailSent || sendingEmail}
                className="flex items-center justify-center gap-2 p-3.5 rounded-xl text-[13px] font-bold text-white transition-colors"
                style={{ background: emailSent ? "#059669" : C.navy, cursor: "pointer", border: "none", opacity: sendingEmail ? 0.7 : 1 }}>
                {sendingEmail ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : emailSent ? <><CheckCircle2 size={16} /> Sent</> : <><Send size={16} /> Send Email</>}
              </button>
            </div>
          </div>
        </Card>
      )}

      {/* Step 1: Template Selection */}
      <Card className="p-0 mb-5 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: `1px solid ${C.g100}` }}>
          <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[13px] font-bold text-white flex-shrink-0" style={{ background: tpl ? "#059669" : C.navy }}>
            {tpl ? <CheckCircle2 size={16} /> : "1"}
          </div>
          <h3 className="text-[15px] font-bold" style={{ color: C.g900 }}>Select Template</h3>
          <span className="text-[12px] font-semibold px-2 py-0.5 rounded-full ml-auto" style={{ background: C.g100, color: C.g500 }}>{templates.length} available</span>
        </div>
        <div className="p-5">
          {loadingTpl ? (
            <div className="py-6 text-center"><Loader2 size={20} className="mx-auto animate-spin" style={{ color: C.navy }} /></div>
          ) : (
            <div className="space-y-2.5">
              {templates.map(t => (
                <div key={t.id} className="flex items-center gap-3">
                  <button onClick={() => setTpl(t.id)} className="flex-1 flex items-center gap-4 p-4 rounded-xl text-left transition-all" style={{ border: `2px solid ${tpl === t.id ? C.navy : C.g200}`, background: tpl === t.id ? C.navyFaint : "white" }}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: t.isSystem ? C.g100 : C.navyFaint }}>
                      <FileText size={18} style={{ color: t.isSystem ? C.g500 : C.navy }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-[15px] font-bold" style={{ color: C.g900 }}>{t.name}</p>
                        {t.isSystem && <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ background: C.g100, color: C.g500 }}>SYSTEM</span>}
                      </div>
                      <p className="text-[12px]" style={{ color: C.g500 }}>{t.isSystem ? "AI-generated using report data" : "Custom template with merge fields"}</p>
                    </div>
                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ border: `2px solid ${tpl === t.id ? C.navy : C.g300}` }}>
                      {tpl === t.id && <div className="w-2.5 h-2.5 rounded-full" style={{ background: C.navy }} />}
                    </div>
                  </button>
                  {!t.isSystem && (
                    <div className="flex flex-col gap-1">
                      <button onClick={() => { setEditingTemplate(t); setShowTemplateEditor(true); }} className="px-3 py-1.5 rounded-lg text-[11px] font-bold" style={{ border: `1px solid ${C.g200}`, color: C.g600, background: "white", cursor: "pointer" }}>Edit</button>
                      <button onClick={() => handleDeleteTemplate(t.id)} disabled={deletingTplId === t.id} className="px-3 py-1.5 rounded-lg text-[11px] font-bold" style={{ border: `1px solid #fca5a5`, color: C.red, background: "white", cursor: "pointer", opacity: deletingTplId === t.id ? 0.6 : 1 }}>{deletingTplId === t.id ? <Loader2 size={12} className="animate-spin inline" /> : "Delete"}</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Step 2: Offer Tier Selection */}
      {tiers.length > 0 && (
        <Card className="p-0 mb-5 overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: `1px solid ${C.g100}` }}>
            <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[13px] font-bold text-white flex-shrink-0" style={{ background: selectedTier ? "#059669" : C.navy }}>
              {selectedTier ? <CheckCircle2 size={16} /> : "2"}
            </div>
            <h3 className="text-[15px] font-bold" style={{ color: C.g900 }}>Select Offer Tier</h3>
          </div>
          <div className="p-5 space-y-3">
            {tiers.map((t, i) => {
              const isSelected = selectedTier === t.name;
              return (
                <button key={i} onClick={() => setSelectedTier(t.name)}
                  className="w-full flex items-center gap-4 p-4 rounded-2xl text-left transition-all"
                  style={{ border: `2px solid ${isSelected ? tierBorder(t) : C.g200}`, background: isSelected ? tierBg(t) : "white" }}>
                  <div className="w-2 h-14 rounded-full flex-shrink-0" style={{ background: tierAccent(t), opacity: isSelected ? 1 : 0.3 }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-[15px] font-bold" style={{ color: C.g900 }}>{t.name}</p>
                      {t.rec && <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ background: `${C.navy}15`, color: C.navy }}>RECOMMENDED</span>}
                    </div>
                    <p className="text-[13px] mt-0.5" style={{ color: C.g500 }}>{t.irr}</p>
                    {t.perUnit && <p className="text-[12px] mt-0.5" style={{ color: C.g400 }}>{t.perUnit}</p>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[22px] font-bold" style={{ color: isSelected ? tierAccent(t) : C.g900 }}>{t.price}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {/* Generation Mode (custom templates only) */}
      {selectedTpl && !selectedTpl.isSystem && (
        <Card className="p-5 mb-5">
          <h3 className="text-[12px] font-bold uppercase tracking-widest mb-3" style={{ color: C.g400 }}>Generation Mode</h3>
          <div className="flex gap-3">
            {([{ key: "template" as const, label: "Template Merge", desc: "Replace placeholders with report data" }, { key: "ai" as const, label: "System Generated", desc: "Generate content using the template as a guide" }]).map(m => (
              <button key={m.key} onClick={() => setMode(m.key)} className="flex-1 p-3.5 rounded-xl text-left" style={{ border: `2px solid ${mode === m.key ? C.navy : C.g200}`, background: mode === m.key ? C.navyFaint : "white" }}>
                <p className="text-[14px] font-bold" style={{ color: C.g900 }}>{m.label}</p>
                <p className="text-[12px]" style={{ color: C.g500 }}>{m.desc}</p>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Step 3: LOI Options */}
      <Card className="p-0 mb-5 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: `1px solid ${C.g100}` }}>
          <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[13px] font-bold text-white flex-shrink-0" style={{ background: C.navy }}>3</div>
          <h3 className="text-[15px] font-bold" style={{ color: C.g900 }}>LOI Options</h3>
        </div>
        <div className="p-5 space-y-3">
          {([
            { l: "Contingencies", s: c1, set: setC1, text: c1Text, setText: setC1Text },
            { l: "Earnest Money", s: c2, set: setC2, text: c2Text, setText: setC2Text },
            { l: "Closing Timeline", s: c3, set: setC3, text: c3Text, setText: setC3Text },
          ] as const).map((o, idx) => (
            <div key={o.l} className="rounded-xl overflow-hidden" style={{ border: `1.5px solid ${o.s ? `${optionColors[idx]}30` : C.g200}`, borderLeft: `4px solid ${o.s ? optionColors[idx] : C.g300}` }}>
              <label className="flex items-center gap-4 px-4 py-3.5 cursor-pointer" style={{ background: o.s ? `${optionColors[idx]}08` : "white" }}>
                <Toggle state={o.s} set={o.set} />
                <span className="text-[15px] font-semibold flex-1" style={{ color: C.g700 }}>{o.l}</span>
                {o.s && !o.text.trim() && <span className="text-[11px] font-bold" style={{ color: C.red }}>Empty</span>}
              </label>
              {o.s && (
                <div className="px-4 pb-4">
                  <textarea value={o.text} onChange={e => o.setText(e.target.value)} rows={3}
                    className="w-full text-[13px] rounded-xl px-4 py-3 outline-none resize-none"
                    style={{ border: `1.5px solid ${C.g200}`, color: C.g700, background: C.g50 }}
                    onFocus={e => e.target.style.borderColor = C.navy} onBlur={e => e.target.style.borderColor = C.g200} />
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Generate Button & Summary */}
      {!loiId && (
        <Card className="p-0 mb-6 overflow-hidden">
          {tpl && selectedTier && (
            <div className="px-5 py-3 flex items-center gap-3 text-[13px]" style={{ background: C.g50, borderBottom: `1px solid ${C.g100}` }}>
              <span style={{ color: C.g500 }}>Using:</span>
              <span className="font-bold" style={{ color: C.g700 }}>{selectedTpl?.name || "Template"}</span>
              <span style={{ color: C.g300 }}>|</span>
              <span className="font-bold" style={{ color: C.g700 }}>{selectedTier}</span>
              {selectedTierObj && <span className="font-bold" style={{ color: C.navy }}>{selectedTierObj.price}</span>}
            </div>
          )}
          <div className="p-5">
            {!tpl && <p className="text-[13px] mb-3 px-3 py-2 rounded-lg" style={{ background: "#fffbeb", color: "#92400e", border: "1px solid #fde68a" }}>Select a template above to continue</p>}
            {!selectedTier && tpl && <p className="text-[13px] mb-3 px-3 py-2 rounded-lg" style={{ background: "#fffbeb", color: "#92400e", border: "1px solid #fde68a" }}>Select an offer tier above to continue</p>}
            <button disabled={gen || !tpl || !selectedTier} onClick={doGen}
              className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl text-[16px] font-bold text-white transition-opacity disabled:opacity-50"
              style={{ background: `linear-gradient(135deg, ${C.navy}, ${C.navyLight})`, cursor: gen || !tpl || !selectedTier ? "not-allowed" : "pointer", border: "none" }}>
              {gen ? <><Loader2 size={20} className="animate-spin" /> Generating LOI…</> : <><FilePlus size={20} /> Generate Letter of Intent</>}
            </button>
          </div>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-2 text-[15px] font-semibold" style={{ color: C.g500 }}><ChevronLeft size={20} /> Back to Report</button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   TAB ORCHESTRATORS
   ═══════════════════════════════════════════════ */
function EvaluateDealTab({ contactId }: { contactId: string }) {
  const [step, setStep] = useState(0); const [comp, setComp] = useState<number[]>([]);
  const [propType, setPropType] = useState<PropertyType | null>(null);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [fileIds, setFileIds] = useState<string[]>([]);
  const [extractedFields, setExtractedFields] = useState<Record<string, string>>({});
  const [rawExtraction, setRawExtraction] = useState<RawExtractionSnapshot | null>(null);
  const [reportId, setReportId] = useState<string | null>(null);
  const [isStandby, setIsStandby] = useState(false);
  const done = (s: number) => { setComp(p => [...new Set([...p, s])]); setStep(s + 1); };
  return (<div>{step > 0 && <Stepper current={step} completed={comp} />}<div className="mt-2">
    {step === 0 && <PropertyTypeStep onSelect={t => { setPropType(t); done(0); }} />}
    {step === 1 && propType && <UploadStep propertyType={propType} files={files} setFiles={setFiles} onNext={(ids) => { setFileIds(ids); done(1); }} onBack={() => setStep(0)} />}
    {step === 2 && propType && <ReviewFieldsStep propertyType={propType} fileIds={fileIds} uploadedFiles={files} onNext={(payload, standby) => { setExtractedFields(payload.fields); setRawExtraction(payload.rawExtraction); setIsStandby(!!standby); done(2); }} onBack={() => setStep(1)} />}
    {step === 3 && propType && <AnalysisStep propertyType={propType} contactId={contactId} fields={extractedFields} fileIds={fileIds} rawExtraction={rawExtraction} standby={isStandby} onComplete={(id) => { setReportId(id); done(3); }} />}
    {step === 4 && propType && <ReportStep propertyType={propType} reportId={reportId} contactId={contactId} onGoToLOI={() => done(4)} />}
    {step === 5 && propType && <LOIStep propertyType={propType} reportId={reportId} contactId={contactId} onBack={() => setStep(4)} />}
  </div></div>);
}

function ReportsTab({ contactId }: { contactId: string }) {
  const api = useApi(); const toast = useToast();
  const [reports, setReports] = useState<Array<{ id: string; propertyName?: string; propertyAddress?: string; propertyType: string; crmFields: Array<{ key: string; label: string; val: string }>; createdAt: string; archived?: boolean; pdfUrl?: string; downloadUrl?: string; legacySource?: string }>>([]);
  const [viewReport, setViewReport] = useState<{ id: string; propertyType: PropertyType } | null>(null);
  const [loiReport, setLoiReport] = useState<{ id: string; propertyType: PropertyType } | null>(null);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [busyAction, setBusyAction] = useState<{ id: string; type: "archive" | "delete" } | null>(null);
  const [downloadingReportId, setDownloadingReportId] = useState<string | null>(null);
  const loadReports = useCallback(() => { api.getReports(contactId).then(r => setReports(r.reports)).catch(() => { }); }, [contactId, api]);
  useEffect(loadReports, [loadReports]);

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this report? This cannot be undone.")) return;
    setBusyAction({ id, type: "delete" });
    try { await api.deleteReport(id); toast("Report deleted"); loadReports(); } catch (e) { toast(`Delete failed: ${(e as Error).message}`, "error"); }
    finally { setBusyAction(null); }
  };
  const handleArchive = async (id: string, archived: boolean) => {
    setBusyAction({ id, type: "archive" });
    try { await api.archiveReport(id, !archived); toast(archived ? "Report unarchived" : "Report archived"); loadReports(); } catch (e) { toast(`Failed: ${(e as Error).message}`, "error"); }
    finally { setBusyAction(null); }
  };
  const handleDownload = async (report: { id: string; propertyName?: string }) => {
    setDownloadingReportId(report.id);
    try {
      const blob = await api.downloadReport(report.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${report.propertyName || "Analysis Report"}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast(`Download failed: ${(e as Error).message}`, "error");
    } finally {
      setDownloadingReportId(null);
    }
  };

  const filtered = reports.filter(r => {
    if (!showArchived && r.archived) return false;
    if (showArchived && !r.archived) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (r.propertyName || "").toLowerCase().includes(q) || (r.propertyAddress || "").toLowerCase().includes(q) || r.propertyType.toLowerCase().includes(q);
  });

  if (loiReport) return <LOIStep propertyType={loiReport.propertyType} reportId={loiReport.id} contactId={contactId} onBack={() => setLoiReport(null)} />;
  if (viewReport) return <div><button onClick={() => setViewReport(null)} className="flex items-center gap-2 text-[15px] font-semibold mb-4" style={{ color: C.g500 }}><ChevronLeft size={18} /> Back</button><ReportStep propertyType={viewReport.propertyType} reportId={viewReport.id} contactId={contactId} onGoToLOI={() => setLoiReport(viewReport)} /></div>;
  return <div>
    <div className="flex items-center justify-between mb-5">
      <h2 className="text-[24px] font-bold" style={{ color: C.g900 }}>Reports</h2>
      <div className="flex items-center gap-3">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: C.g400 }} />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search reports…" className="pl-9 pr-4 py-2.5 rounded-xl text-[13px] border outline-none" style={{ borderColor: C.g200, color: C.g700, width: "220px" }} onFocus={e => e.target.style.borderColor = C.navy} onBlur={e => e.target.style.borderColor = C.g200} />
        </div>
        <button onClick={() => setShowArchived(!showArchived)} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-bold" style={{ background: showArchived ? C.navyFaint : "white", color: showArchived ? C.navy : C.g500, border: `1.5px solid ${showArchived ? C.navy : C.g200}`, cursor: "pointer" }}>
          <Archive size={15} /> {showArchived ? "Archived" : "Active"}
        </button>
      </div>
    </div>
    <Card className="overflow-hidden">
      <table className="w-full">
        <thead>
          <tr style={{ background: C.g50 }}>
            {["Property", "Type", "Date", ""].map((h, i) => (
              <th key={i} className="text-left text-[12px] font-bold uppercase tracking-wider px-6 py-4" style={{ color: C.g400 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr><td colSpan={4} className="px-6 py-8 text-center text-[15px]" style={{ color: C.g400 }}>{search ? "No reports match your search" : showArchived ? "No archived reports" : "No reports yet"}</td></tr>
          ) : filtered.map(r => {
            const rowBusy = busyAction?.id === r.id;
            const isArchiving = rowBusy && busyAction?.type === "archive";
            const isDeleting = rowBusy && busyAction?.type === "delete";
            const isDownloading = downloadingReportId === r.id;
            return (
              <tr key={r.id} className="hover:bg-gray-50" style={{ borderTop: `1px solid ${C.g100}` }}>
                <td className="px-6 py-4">
                  <p className="text-[16px] font-bold" style={{ color: C.g900 }}>{r.propertyName || "Property"}</p>
                  <p className="text-[14px]" style={{ color: C.g500 }}>{r.propertyAddress || ""}</p>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Badge variant="warning">{r.propertyType}</Badge>
                    {r.legacySource && <Badge variant="info">Legacy PDF</Badge>}
                  </div>
                </td>
                <td className="px-6 py-4 text-[15px]" style={{ color: C.g500 }}>{new Date(r.createdAt).toLocaleDateString()}</td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => setViewReport({ id: r.id, propertyType: (r.propertyType === "single" ? "single" : "multi") as PropertyType })} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[14px] font-bold border hover:bg-gray-50" style={{ borderColor: C.g200, color: C.g600 }}><Eye size={16} /> View</button>
                    <button onClick={() => handleDownload(r)} disabled={isDownloading} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[14px] font-bold border hover:bg-gray-50" style={{ borderColor: C.g200, color: C.navy, opacity: isDownloading ? 0.65 : 1 }}>{isDownloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Download</button>
                    <button onClick={() => handleArchive(r.id, !!r.archived)} disabled={rowBusy} className="p-2.5 rounded-lg hover:bg-gray-100" title={r.archived ? "Unarchive" : "Archive"} style={{ color: C.g400, opacity: rowBusy ? 0.5 : 1 }}>{isArchiving ? <Loader2 size={16} className="animate-spin" /> : <Archive size={16} />}</button>
                    <button onClick={() => handleDelete(r.id)} disabled={rowBusy} className="p-2.5 rounded-lg hover:bg-red-50" title="Delete" style={{ color: "#ef4444", opacity: rowBusy ? 0.5 : 1 }}>{isDeleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}</button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  </div>;
}

function LOIsTab({ contactId }: { contactId: string }) {
  const api = useApi(); const toast = useToast();
  const [lois, setLois] = useState<Array<{ id: string; address?: string; offerPrice?: string; template?: string; createdAt: string; status: string; downloadUrl?: string }>>([]);
  const [templates, setTemplates] = useState<LOITemplate[]>([]);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<LOITemplate | null>(null);
  const [view, setView] = useState<"lois" | "templates">("lois");
  const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(null);

  useEffect(() => { api.getLOIs(contactId).then(r => setLois(r.lois)).catch(() => { }); }, [contactId, api]);
  const loadTemplates = useCallback(() => { api.getTemplates().then(r => setTemplates(r.templates)).catch(() => { }); }, [api]);
  useEffect(loadTemplates, [loadTemplates]);

  const handleDeleteTemplate = async (id: string) => {
    setDeletingTemplateId(id);
    try { await api.deleteTemplate(id); toast("Template deleted"); loadTemplates(); } catch (e) { toast(`Delete failed: ${(e as Error).message}`, "error"); }
    finally { setDeletingTemplateId(null); }
  };
  const [tplSearch, setTplSearch] = useState("");
  const filteredTemplates = templates.filter(t => {
    if (!tplSearch.trim()) return true;
    const q = tplSearch.toLowerCase();
    return t.name.toLowerCase().includes(q) || t.propertyType.toLowerCase().includes(q);
  });
  const [previewing, setPreviewing] = useState<string | null>(null);
  const handlePreview = async (id: string) => {
    setPreviewing(id);
    try {
      const blob = await api.previewTemplate(id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast(`Preview failed: ${(e as Error).message}`, "error"); }
    finally { setPreviewing(null); }
  };

  return <div>
    {showTemplateEditor && (
      <Suspense fallback={<div style={{ position: "fixed", inset: 0, background: "rgba(15,23,36,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}><Loader2 size={32} className="animate-spin" style={{ color: "white" }} /></div>}>
        <LOITemplateEditor
          template={editingTemplate}
          onSave={() => { setShowTemplateEditor(false); setEditingTemplate(null); loadTemplates(); toast("Template saved"); }}
          onClose={() => { setShowTemplateEditor(false); setEditingTemplate(null); }}
        />
      </Suspense>
    )}
    <div className="flex items-center justify-between mb-5">
      <h2 className="text-[24px] font-bold" style={{ color: C.g900 }}>Letters of Intent</h2>
      <div className="flex items-center gap-2">
        <div style={{ visibility: view === "templates" ? "visible" : "hidden" }}>
          <button onClick={() => { setEditingTemplate(null); setShowTemplateEditor(true); }} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-bold transition-all" style={{ background: C.navy, color: "white", border: "none", cursor: "pointer" }}><FilePlus size={15} /> New Template</button>
        </div>
        <div className="flex p-1 rounded-xl" style={{ background: C.g50 }}>
          <button onClick={() => setView("templates")} className="px-4 py-2 rounded-lg text-[13px] font-bold" style={{ background: view === "templates" ? "white" : "transparent", color: view === "templates" ? C.navy : C.g500, boxShadow: view === "templates" ? "0 1px 3px rgba(0,0,0,0.08)" : "none" }}>Templates</button>
          <button onClick={() => setView("lois")} className="px-4 py-2 rounded-lg text-[13px] font-bold" style={{ background: view === "lois" ? "white" : "transparent", color: view === "lois" ? C.navy : C.g500, boxShadow: view === "lois" ? "0 1px 3px rgba(0,0,0,0.08)" : "none" }}>LOIs</button>
        </div>
      </div>
    </div>

    {view === "lois" && (
      <Card className="overflow-hidden"><table className="w-full"><thead><tr style={{ background: C.g50 }}>{["Property", "Offer", "Template", "Date", "Status", ""].map((h, i) => <th key={i} className="text-left text-[12px] font-bold uppercase tracking-wider px-6 py-4" style={{ color: C.g400 }}>{h}</th>)}</tr></thead><tbody>{lois.length === 0 ? <tr><td colSpan={6} className="px-6 py-8 text-center text-[15px]" style={{ color: C.g400 }}>No LOIs yet</td></tr> : lois.map(l => <tr key={l.id} className="hover:bg-gray-50" style={{ borderTop: `1px solid ${C.g100}` }}><td className="px-6 py-4 text-[16px] font-bold" style={{ color: C.g900 }}>{l.address || "Property"}</td><td className="px-6 py-4 text-[16px] font-bold" style={{ color: C.navy }}>{l.offerPrice || "—"}</td><td className="px-6 py-4 text-[15px]" style={{ color: C.g600 }}>{l.template || "—"}</td><td className="px-6 py-4 text-[15px]" style={{ color: C.g500 }}>{new Date(l.createdAt).toLocaleDateString()}</td><td className="px-6 py-4"><Badge variant="info">{l.status.toUpperCase()}</Badge></td><td className="px-6 py-4 text-right flex items-center justify-end gap-2"><button onClick={() => l.downloadUrl && window.open(l.downloadUrl, "_blank")} className="p-2.5 rounded-lg hover:bg-gray-100" style={{ color: C.g400 }}><Download size={18} /></button><button onClick={() => toast("Use LOI Send button from the LOI generator")} className="p-2.5 rounded-lg hover:bg-gray-100" style={{ color: C.g400 }}><Send size={18} /></button></td></tr>)}</tbody></table></Card>
    )}

    {view === "templates" && (
      <div>
        <div className="mb-4">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: C.g400 }} />
            <input type="text" value={tplSearch} onChange={e => setTplSearch(e.target.value)} placeholder="Search templates…" className="pl-9 pr-4 py-2.5 rounded-xl text-[13px] border outline-none w-full" style={{ borderColor: C.g200, color: C.g700 }} onFocus={e => e.target.style.borderColor = C.navy} onBlur={e => e.target.style.borderColor = C.g200} />
          </div>
        </div>
        <div className="space-y-3">
          {filteredTemplates.length === 0 ? <Card className="p-8 text-center"><p className="text-[15px]" style={{ color: C.g400 }}>{tplSearch ? "No templates match your search" : "No templates yet. Create your first LOI template."}</p></Card> : filteredTemplates.map(t => (
            <Card key={t.id} className="p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: t.isSystem ? C.g100 : `${C.navy}12` }}>
                <FileText size={20} style={{ color: t.isSystem ? C.g500 : C.navy }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-[16px] font-bold truncate" style={{ color: C.g900 }}>{t.name}</p>
                  {t.isSystem && <span className="text-[10px] font-bold px-2 py-0.5 rounded flex-shrink-0" style={{ background: C.g100, color: C.g500 }}>SYSTEM</span>}
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded flex-shrink-0" style={{ background: t.propertyType === "multi" ? `${C.navy}12` : "#f0fdf4", color: t.propertyType === "multi" ? C.navy : "#16a34a" }}>{t.propertyType === "multi" ? "MF" : "SF"}</span>
                </div>
                <p className="text-[13px]" style={{ color: C.g500 }}>{t.isSystem ? "System default template" : `Custom template — updated ${new Date(t.updatedAt).toLocaleDateString()}`}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => handlePreview(t.id)} disabled={previewing === t.id} className="px-4 py-2 rounded-lg text-[13px] font-semibold flex items-center gap-1.5" style={{ border: `1.5px solid ${C.g200}`, color: C.g600, background: "white", cursor: "pointer" }}>{previewing === t.id ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Preview</button>
                <button onClick={() => { setEditingTemplate(t); setShowTemplateEditor(true); }} className="px-4 py-2 rounded-lg text-[13px] font-semibold" style={{ border: `1.5px solid ${C.g200}`, color: C.g600, background: "white", cursor: "pointer" }}>Edit</button>
                {t.isSystem && <button onClick={() => { setEditingTemplate(t); setShowTemplateEditor(true); }} className="px-4 py-2 rounded-lg text-[13px] font-semibold" style={{ border: `1.5px solid ${C.g200}`, color: C.g600, background: "white", cursor: "pointer" }}>Duplicate</button>}
                <button onClick={() => handleDeleteTemplate(t.id)} disabled={deletingTemplateId === t.id} className="p-2.5 rounded-lg hover:bg-red-50 disabled:opacity-60" title="Delete" style={{ color: "#ef4444" }}>{deletingTemplateId === t.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}</button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    )}
  </div>;
}

function TemplatesTab() {
  const api = useApi(); const toast = useToast();
  const [templates, setTemplates] = useState<LOITemplate[]>([]);
  const [loadingTpls, setLoadingTpls] = useState(true);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<LOITemplate | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [previewing, setPreviewing] = useState<string | null>(null);

  // LOI defaults & disclaimers (saved per-location)
  const [defaults, setDefaults] = useState({
    loiContingenciesText: "", loiEarnestText: "", loiTimelineText: "",
    loiDisclaimer: "", reportDisclaimer: "",
  });
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [savedDefaults, setSavedDefaults] = useState(false);

  useEffect(() => {
    api.getSettings().then(r => {
      const s = r.settings;
      setDefaults({
        loiContingenciesText: s.loiContingenciesText || "",
        loiEarnestText: s.loiEarnestText || "",
        loiTimelineText: s.loiTimelineText || "",
        loiDisclaimer: s.loiDisclaimer || "",
        reportDisclaimer: s.reportDisclaimer || "",
      });
    }).catch(() => {});
  }, []);

  const saveDefaults = async () => {
    setSavingDefaults(true);
    try { await api.updateSettings(defaults); setSavedDefaults(true); toast("Defaults saved"); setTimeout(() => setSavedDefaults(false), 2000); }
    catch (e) { toast(`Save failed: ${(e as Error).message}`, "error"); }
    finally { setSavingDefaults(false); }
  };

  const loadTemplates = useCallback(() => {
    setLoadingTpls(true);
    api.getTemplates().then(r => { setTemplates(r.templates); setLoadingTpls(false); }).catch(() => setLoadingTpls(false));
  }, [api]);
  useEffect(loadTemplates, [loadTemplates]);

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this template?")) return;
    setDeletingId(id);
    try { await api.deleteTemplate(id); toast("Template deleted"); loadTemplates(); }
    catch (e) { toast(`Delete failed: ${(e as Error).message}`, "error"); }
    finally { setDeletingId(null); }
  };
  const handlePreview = async (id: string) => {
    setPreviewing(id);
    try {
      const blob = await api.previewTemplate(id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast(`Preview failed: ${(e as Error).message}`, "error"); }
    finally { setPreviewing(null); }
  };

  const filtered = templates.filter(t => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return t.name.toLowerCase().includes(q) || t.propertyType.toLowerCase().includes(q);
  });

  const optionColors = ["#059669", "#2563eb", "#d97706"];

  return <div className="max-w-3xl">
    {showTemplateEditor && (
      <Suspense fallback={<div style={{ position: "fixed", inset: 0, background: "rgba(15,23,36,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}><Loader2 size={32} className="animate-spin" style={{ color: "white" }} /></div>}>
        <LOITemplateEditor
          template={editingTemplate}
          onSave={() => { setShowTemplateEditor(false); setEditingTemplate(null); loadTemplates(); toast("Template saved"); }}
          onClose={() => { setShowTemplateEditor(false); setEditingTemplate(null); }}
        />
      </Suspense>
    )}

    {/* LOI Default Options */}
    <Card className="p-6 mb-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${C.navy}12` }}><FileText size={18} style={{ color: C.navy }} /></div>
          <div><h3 className="text-[16px] font-bold" style={{ color: C.g900 }}>LOI Default Options</h3><p className="text-[13px]" style={{ color: C.g500 }}>Default text applied to all new LOIs</p></div>
        </div>
        <button onClick={saveDefaults} disabled={savingDefaults} className="flex items-center gap-2 px-4 py-2 rounded-xl text-[13px] font-bold text-white" style={{ background: savedDefaults ? "#059669" : C.navy, opacity: savingDefaults ? 0.7 : 1, cursor: "pointer" }}>
          {savingDefaults ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : savedDefaults ? <><CheckCircle2 size={14} /> Saved</> : <><Save size={14} /> Save Defaults</>}
        </button>
      </div>
      {([
        { key: "loiContingenciesText" as const, label: "Contingencies", color: optionColors[0] },
        { key: "loiEarnestText" as const, label: "Earnest Money", color: optionColors[1] },
        { key: "loiTimelineText" as const, label: "Closing Timeline", color: optionColors[2] },
      ]).map(opt => (
        <div key={opt.key} className="mb-4">
          <label className="flex items-center gap-2 text-[13px] font-bold mb-2" style={{ color: C.g600 }}>
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: opt.color }} />
            {opt.label}
          </label>
          <textarea
            value={defaults[opt.key]}
            onChange={e => setDefaults(p => ({ ...p, [opt.key]: e.target.value }))}
            rows={3}
            className="w-full px-4 py-3 rounded-xl text-[14px] outline-none resize-none"
            style={{ border: `1.5px solid ${C.g200}`, color: C.g900 }}
            onFocus={e => e.target.style.borderColor = C.navy}
            onBlur={e => e.target.style.borderColor = C.g200}
          />
        </div>
      ))}
      <div className="mt-5 pt-5" style={{ borderTop: `1.5px solid ${C.g100}` }}>
        <h4 className="text-[12px] font-bold uppercase tracking-widest mb-4" style={{ color: C.g400 }}>Disclaimers</h4>
        <div className="mb-4">
          <label className="flex items-center gap-2 text-[13px] font-bold mb-2" style={{ color: C.g600 }}>
            <Shield size={14} style={{ color: C.red }} />
            LOI Disclaimer
          </label>
          <textarea
            value={defaults.loiDisclaimer}
            onChange={e => setDefaults(p => ({ ...p, loiDisclaimer: e.target.value }))}
            rows={4}
            className="w-full px-4 py-3 rounded-xl text-[14px] outline-none resize-none"
            style={{ border: `1.5px solid ${C.g200}`, color: C.g900 }}
            onFocus={e => e.target.style.borderColor = C.navy}
            onBlur={e => e.target.style.borderColor = C.g200}
          />
        </div>
        <div>
          <label className="flex items-center gap-2 text-[13px] font-bold mb-2" style={{ color: C.g600 }}>
            <Shield size={14} style={{ color: "#d97706" }} />
            Report Disclaimer
          </label>
          <textarea
            value={defaults.reportDisclaimer}
            onChange={e => setDefaults(p => ({ ...p, reportDisclaimer: e.target.value }))}
            rows={3}
            className="w-full px-4 py-3 rounded-xl text-[14px] outline-none resize-none"
            style={{ border: `1.5px solid ${C.g200}`, color: C.g900 }}
            onFocus={e => e.target.style.borderColor = C.navy}
            onBlur={e => e.target.style.borderColor = C.g200}
          />
        </div>
      </div>
    </Card>

    {/* LOI Templates */}
    <div className="flex items-center justify-between mb-5">
      <h2 className="text-[24px] font-bold" style={{ color: C.g900 }}>LOI Templates</h2>
      <button onClick={() => { setEditingTemplate(null); setShowTemplateEditor(true); }} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-bold text-white" style={{ background: C.navy, cursor: "pointer" }}><FilePlus size={15} /> New Template</button>
    </div>
    <div className="mb-4 relative">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: C.g400 }} />
      <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search templates…" className="pl-9 pr-4 py-2.5 rounded-xl text-[13px] border outline-none w-full" style={{ borderColor: C.g200, color: C.g700 }} onFocus={e => e.target.style.borderColor = C.navy} onBlur={e => e.target.style.borderColor = C.g200} />
    </div>
    {loadingTpls ? (
      <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin" size={28} style={{ color: C.navy }} /></div>
    ) : filtered.length === 0 ? (
      <Card className="p-8 text-center"><p className="text-[15px]" style={{ color: C.g400 }}>{search ? "No templates match your search" : "No templates yet. Create your first LOI template."}</p></Card>
    ) : (
      <div className="space-y-3">
        {filtered.map(t => (
          <Card key={t.id} className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: t.isSystem ? C.g100 : `${C.navy}12` }}>
              <FileText size={20} style={{ color: t.isSystem ? C.g500 : C.navy }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-[16px] font-bold truncate" style={{ color: C.g900 }}>{t.name}</p>
                {t.isSystem && <span className="text-[10px] font-bold px-2 py-0.5 rounded flex-shrink-0" style={{ background: C.g100, color: C.g500 }}>SYSTEM</span>}
                <span className="text-[10px] font-bold px-2 py-0.5 rounded flex-shrink-0" style={{ background: t.propertyType === "multi" ? `${C.navy}12` : "#f0fdf4", color: t.propertyType === "multi" ? C.navy : "#16a34a" }}>{t.propertyType === "multi" ? "MF" : "SF"}</span>
              </div>
              <p className="text-[13px]" style={{ color: C.g500 }}>{t.isSystem ? "System default template" : `Custom template — updated ${new Date(t.updatedAt).toLocaleDateString()}`}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={() => handlePreview(t.id)} disabled={previewing === t.id} className="px-4 py-2 rounded-lg text-[13px] font-semibold flex items-center gap-1.5" style={{ border: `1.5px solid ${C.g200}`, color: C.g600, background: "white", cursor: "pointer" }}>{previewing === t.id ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Preview</button>
              <button onClick={() => { setEditingTemplate(t); setShowTemplateEditor(true); }} className="px-4 py-2 rounded-lg text-[13px] font-semibold" style={{ border: `1.5px solid ${C.g200}`, color: C.g600, background: "white", cursor: "pointer" }}>{t.isSystem ? "Duplicate" : "Edit"}</button>
              {!t.isSystem && <button onClick={() => handleDelete(t.id)} disabled={deletingId === t.id} className="p-2.5 rounded-lg hover:bg-red-50 disabled:opacity-60" title="Delete" style={{ color: "#ef4444" }}>{deletingId === t.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}</button>}
            </div>
          </Card>
        ))}
      </div>
    )}
  </div>;
}

function SettingsTab() {
  const api = useApi(); const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<Partial<InvestorSettings>>({
    holdPeriod: 5,
    tier3Irr: 15, tier2Irr: 18, tier1Irr: 22,
    tier3Label: "Conservative", tier2Label: "Target", tier1Label: "Stretch",
    tier3Assumption: "conservative", tier2Assumption: "realistic", tier1Assumption: "realistic",
    targetCoc: null, targetEquityMultiple: null, targetDscr: 1.25,
    preferredFinancing: "best_fit", exitStrategy: "sale", customNotes: "",
  });

  useEffect(() => {
    api.getSettings().then(r => { setSettings(r.settings); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const update = (key: keyof InvestorSettings, val: unknown) => setSettings(prev => ({ ...prev, [key]: val }));
  const saveSettings = async () => {
    setSaving(true);
    try { await api.updateSettings(settings); toast("Investment criteria saved", "success"); }
    catch (e) { toast(e instanceof Error ? e.message : "Save failed", "error"); }
    finally { setSaving(false); }
  };

  const numInput = (label: string, key: keyof InvestorSettings, suffix = "", placeholder = "") => (
    <div>
      <label className="block text-[13px] font-bold mb-1.5" style={{ color: C.g600 }}>{label}</label>
      <div className="relative">
        <input type="number" value={settings[key] as number ?? ""} onChange={e => update(key, e.target.value === "" ? null : Number(e.target.value))} placeholder={placeholder}
          className="w-full px-3.5 py-2.5 rounded-xl text-[15px] font-semibold outline-none" style={{ border: `1.5px solid ${C.g200}`, color: C.g900, background: "white" }}
          onFocus={e => e.target.style.borderColor = C.navy} onBlur={e => e.target.style.borderColor = C.g200} />
        {suffix && <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[13px] font-bold" style={{ color: C.g400 }}>{suffix}</span>}
      </div>
    </div>
  );

  const selectInput = (label: string, key: keyof InvestorSettings, options: { value: string; label: string }[]) => (
    <div>
      <label className="block text-[13px] font-bold mb-1.5" style={{ color: C.g600 }}>{label}</label>
      <select value={settings[key] as string ?? ""} onChange={e => update(key, e.target.value)}
        className="w-full px-3.5 py-2.5 rounded-xl text-[15px] font-semibold outline-none appearance-none cursor-pointer"
        style={{ border: `1.5px solid ${C.g200}`, color: C.g900, background: "white", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%239CA3AF' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10z'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center" }}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin" size={28} style={{ color: C.navy }} /></div>;

  return <div className="max-w-3xl">
    <div className="flex items-center justify-between mb-6">
      <h2 className="text-[24px] font-bold" style={{ color: C.g900 }}>Settings</h2>
      <Btn onClick={saveSettings} disabled={saving}>{saving ? <><Loader2 className="animate-spin" size={16} /> Saving...</> : <><Save size={16} /> Save Changes</>}</Btn>
    </div>

    {/* Investment Criteria */}
    <Card className="p-6 mb-5">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${C.navy}12` }}><Target size={18} style={{ color: C.navy }} /></div>
        <div><h3 className="text-[16px] font-bold" style={{ color: C.g900 }}>Investment Criteria</h3><p className="text-[13px]" style={{ color: C.g500 }}>Define your target returns and hold strategy</p></div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-5">
        {numInput("Hold Period", "holdPeriod", "years")}
        {numInput("Target DSCR", "targetDscr", "x", "1.25")}
        {numInput("Target Cash-on-Cash", "targetCoc", "%", "Optional")}
        {numInput("Target Equity Multiple", "targetEquityMultiple", "x", "Optional")}
      </div>

      <div className="grid grid-cols-2 gap-4">
        {selectInput("Preferred Financing", "preferredFinancing", [
          { value: "best_fit", label: "Best Fit (Auto)" },
          { value: "agency_perm", label: "Agency / Permanent" },
          { value: "bridge", label: "Bridge Loan" },
        ])}
        {selectInput("Exit Strategy", "exitStrategy", [
          { value: "sale", label: "Sale" },
          { value: "refinance", label: "Refinance" },
          { value: "hold", label: "Long-term Hold" },
        ])}
      </div>
    </Card>

    {/* Offer Tiers */}
    <Card className="p-6 mb-5">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${C.navy}12` }}><TrendingUp size={18} style={{ color: C.navy }} /></div>
        <div><h3 className="text-[16px] font-bold" style={{ color: C.g900 }}>Offer Tiers</h3><p className="text-[13px]" style={{ color: C.g500 }}>Configure IRR targets and assumptions for each tier</p></div>
      </div>

      {([
        { tier: 3, labelKey: "tier3Label" as const, irrKey: "tier3Irr" as const, assumeKey: "tier3Assumption" as const, color: "#16a34a", desc: "Lowest risk, highest price" },
        { tier: 2, labelKey: "tier2Label" as const, irrKey: "tier2Irr" as const, assumeKey: "tier2Assumption" as const, color: C.navy, desc: "Balanced risk/return" },
        { tier: 1, labelKey: "tier1Label" as const, irrKey: "tier1Irr" as const, assumeKey: "tier1Assumption" as const, color: "#dc2626", desc: "Highest return, most aggressive" },
      ]).map(t => (
        <div key={t.tier} className="p-4 rounded-xl mb-3 last:mb-0" style={{ border: `1.5px solid ${C.g100}`, background: C.g50 }}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-[12px] font-bold" style={{ background: t.color }}>T{t.tier}</div>
            <span className="text-[14px] font-bold" style={{ color: C.g900 }}>Tier {t.tier}</span>
            <span className="text-[12px]" style={{ color: C.g500 }}>— {t.desc}</span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[12px] font-bold mb-1" style={{ color: C.g500 }}>Label</label>
              <input type="text" value={settings[t.labelKey] as string ?? ""} onChange={e => update(t.labelKey, e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-[14px] font-semibold outline-none" style={{ border: `1.5px solid ${C.g200}`, color: C.g900, background: "white" }} />
            </div>
            <div>
              <label className="block text-[12px] font-bold mb-1" style={{ color: C.g500 }}>Min IRR Target</label>
              <div className="relative">
                <input type="number" value={settings[t.irrKey] as number ?? ""} onChange={e => update(t.irrKey, Number(e.target.value))}
                  className="w-full px-3 py-2 rounded-lg text-[14px] font-semibold outline-none" style={{ border: `1.5px solid ${C.g200}`, color: C.g900, background: "white" }} />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] font-bold" style={{ color: C.g400 }}>%</span>
              </div>
            </div>
            <div>
              <label className="block text-[12px] font-bold mb-1" style={{ color: C.g500 }}>Assumption</label>
              <select value={settings[t.assumeKey] as string ?? "conservative"} onChange={e => update(t.assumeKey, e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-[14px] font-semibold outline-none appearance-none cursor-pointer"
                style={{ border: `1.5px solid ${C.g200}`, color: C.g900, background: "white", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%239CA3AF' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10z'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center" }}>
                <option value="conservative">Conservative</option>
                <option value="realistic">Realistic</option>
              </select>
            </div>
          </div>
        </div>
      ))}
    </Card>

    {/* Additional Notes */}
    <Card className="p-6 mb-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${C.navy}12` }}><BookOpen size={18} style={{ color: C.navy }} /></div>
        <div><h3 className="text-[16px] font-bold" style={{ color: C.g900 }}>Additional Notes</h3><p className="text-[13px]" style={{ color: C.g500 }}>Custom instructions applied to every analysis</p></div>
      </div>
      <textarea value={settings.customNotes ?? ""} onChange={e => update("customNotes", e.target.value)} rows={3} placeholder="e.g. Always factor in 5% vacancy reserve, prefer value-add deals..."
        className="w-full px-4 py-3 rounded-xl text-[14px] outline-none resize-none" style={{ border: `1.5px solid ${C.g200}`, color: C.g900 }}
        onFocus={e => e.target.style.borderColor = C.navy} onBlur={e => e.target.style.borderColor = C.g200} />
    </Card>

    {/* Auto-Save */}
    <Card className="p-6 mb-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${C.navy}12` }}><Save size={18} style={{ color: C.navy }} /></div>
        <div><h3 className="text-[16px] font-bold" style={{ color: C.g900 }}>Auto-Save</h3><p className="text-[13px]" style={{ color: C.g500 }}>Automatically save reports to contact documents</p></div>
      </div>
      <label className="flex items-center gap-4 cursor-pointer">
        <Toggle state={settings.autoSaveReport !== false} set={(v) => update("autoSaveReport", v)} />
        <div>
          <span className="text-[15px] font-semibold" style={{ color: C.g700 }}>Auto-save report to documents</span>
          <p className="text-[13px]" style={{ color: C.g500 }}>When enabled, analysis reports are automatically saved to contact documents under Extra</p>
        </div>
      </label>
    </Card>

    {/* Usage */}
    <Card className="p-6 mb-5">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${C.navy}12` }}><BarChart3 size={18} style={{ color: C.navy }} /></div>
        <h3 className="text-[16px] font-bold" style={{ color: C.g900 }}>Usage</h3>
      </div>
      {[{ l: "Analyses today", u: 1, m: 10 }, { l: "LOIs today", u: 1, m: 20 }].map(u => (
        <div key={u.l} className="mb-4 last:mb-0">
          <div className="flex justify-between mb-2"><span className="text-[15px]" style={{ color: C.g600 }}>{u.l}</span><span className="text-[14px] font-mono" style={{ color: C.g400 }}>{u.u}/{u.m}</span></div>
          <div className="h-2.5 rounded-full overflow-hidden" style={{ background: C.g100 }}><div className="h-full rounded-full" style={{ width: `${(u.u / u.m) * 100}%`, background: C.navy }} /></div>
        </div>
      ))}
    </Card>

  </div>;
}

/* ═══════════════════════════════════════════════
   MAIN APP
   ═══════════════════════════════════════════════ */
const TABS: { key: string; label: string; icon: LucideIcon }[] = [
  { key: "evaluate", label: "Evaluate Deal", icon: Target }, { key: "reports", label: "Reports", icon: FileText },
  { key: "lois", label: "LOIs", icon: Briefcase }, { key: "templates", label: "Templates", icon: FilePlus },
  { key: "settings", label: "Settings", icon: Settings },
];

/* ═══════════════════════════════════════════════
   GHL IFRAME APP (Version 1)
   Requires locationId + contactId from URL
   ═══════════════════════════════════════════════ */
function GHLApp() {
  const { contact, authStatus, installUrl, hasContext } = useCRMContext();

  if (authStatus === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#f4f5f7", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
        <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
        <Loader2 size={36} className="animate-spin" style={{ color: C.navy }} />
      </div>
    );
  }

  if ((authStatus === "disconnected" || authStatus === "expired") && !hasContext) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#f4f5f7", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
        <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{ background: authStatus === "expired" ? "#fffbeb" : C.redFaint }}>
            <AlertCircle size={32} style={{ color: authStatus === "expired" ? "#d97706" : C.red }} />
          </div>
          <h2 className="text-[22px] font-bold mb-2" style={{ color: C.g900 }}>
            {authStatus === "expired" ? "Connection Expired" : "Connect to GoHighLevel"}
          </h2>
          <p className="text-[15px] mb-6" style={{ color: C.g500 }}>
            {authStatus === "expired"
              ? "Your GoHighLevel connection has expired. Please reconnect to continue."
              : "This app needs to connect to your GoHighLevel account to access contacts and documents."}
          </p>
          {installUrl && (
            <a href={installUrl} className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl text-[16px] font-semibold text-white shadow-xl hover:opacity-90" style={{ background: C.navy, boxShadow: `0 8px 24px ${C.navy}40`, textDecoration: "none" }}>
              <Link2 size={18} /> {authStatus === "expired" ? "Reconnect" : "Connect GoHighLevel"}
            </a>
          )}
        </div>
      </div>
    );
  }

  if (!hasContext) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#f4f5f7", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
        <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{ background: C.navyFaint }}>
            <AlertCircle size={32} style={{ color: C.navy }} />
          </div>
          <h2 className="text-[22px] font-bold mb-2" style={{ color: C.g900 }}>Missing Context</h2>
          <p className="text-[15px]" style={{ color: C.g500 }}>
            This app requires a valid locationId and contactId. Please open it from within a GoHighLevel contact view.
          </p>
        </div>
      </div>
    );
  }
  return <WorkspaceShell contact={contact} />;
}

/* ═══════════════════════════════════════════════
   STANDALONE APP (Version 2)
   Login → Dashboard → Upload → Analyze →
   Contact auto-extracted from docs → Edit → Results
   Uses separate /api/app/* routes and DB collections
   ═══════════════════════════════════════════════ */

// Build an api adapter from standaloneApi matching the `api` shape
const appApiAdapter: typeof api = {
  getContact: (contactId: string) =>
    standaloneApi.getContact(contactId).then(r => ({ contact: { id: r.contact._id, name: r.contact.name, email: r.contact.email, company: r.contact.company } })),
  saveCRMFields: () => Promise.resolve({ success: true }),
  uploadFiles: (files: File[]) => standaloneApi.uploadFiles(files),
  uploadImage: api.uploadImage, // shares upload endpoint
  extractFields: (body: { fileIds: string[]; propertyType: string }) => standaloneApi.extractFields(body),
  startAnalysis: (body: AnalysisStartBody) =>
    standaloneApi.startAnalysis(body),
  pollAnalysis: (jobId: string) => standaloneApi.pollAnalysis(jobId),
  getReport: (reportId: string) => standaloneApi.getReport(reportId),
  getAnalysisTrace: (reportId: string) => standaloneApi.getAnalysisTrace(reportId),
  getReports: (_contactId: string) => standaloneApi.getReports(),
  saveReportToDocs: () => Promise.resolve({ documentId: '' }),
  downloadReport: (reportId: string) => standaloneApi.downloadReport(reportId),
  deleteReport: (reportId: string) => standaloneApi.deleteReport(reportId),
  archiveReport: (reportId: string, archived: boolean) => standaloneApi.archiveReport(reportId, archived),
  updateReportSection: (reportId: string, sectionKey: string, sectionData: Record<string, unknown>) => standaloneApi.updateReportSection(reportId, sectionKey, sectionData),
  getTemplates: () => standaloneApi.getTemplates(),
  getTemplate: (id: string) => api.getTemplate(id),
  createTemplate: (body: { name: string; propertyType: string; htmlContent: string }) => standaloneApi.createTemplate(body),
  updateTemplate: (id: string, body: { name?: string; htmlContent?: string }) => standaloneApi.updateTemplate(id, body),
  deleteTemplate: (id: string) => standaloneApi.deleteTemplate(id),
  previewTemplate: (id: string) => standaloneApi.previewTemplate(id),
  generateLOI: (body: { contactId: string; reportId: string; templateId: string; tier: string; mode?: 'ai' | 'template'; options: { contingencies: boolean; earnest: boolean; timeline: boolean; contingenciesText?: string; earnestText?: string; timelineText?: string } }) =>
    standaloneApi.generateLOI(body),
  saveLOI: () => Promise.resolve({ documentId: '' }),
  getLOI: (loiId: string) => standaloneApi.getLOI(loiId),
  updateLOIContent: (loiId: string, body: { content: string; htmlContent?: string }) => standaloneApi.updateLOIContent(loiId, body),
  getLOIs: (_contactId: string) => standaloneApi.getLOIs(),
  sendEmail: () => Promise.resolve({ success: true }),
  getCustomFields: () => Promise.resolve({ customFields: [] }),
  createCustomField: () => Promise.resolve({ customField: { id: '', name: '', fieldKey: '', dataType: 'TEXT' as const } }),
  getFieldMappings: () => Promise.resolve({ mappings: [] }),
  saveFieldMappings: () => Promise.resolve({ success: true }),
  saveCustomFieldValues: () => Promise.resolve({ success: true }),
  decryptSSO: () => Promise.resolve({}),
  getAuthStatus: () => Promise.resolve({ connected: true }),
  refreshToken: () => Promise.resolve({ success: true }),
  getSettings: () => standaloneApi.getSettings(),
  updateSettings: (settings: Partial<InvestorSettings>) => standaloneApi.updateSettings(settings),
};

function EmailVerifyScreen({ user, onVerified, onLogout }: { user: StandaloneUser; onVerified: () => void; onLogout: () => void }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  // Poll /me every 5s to check if email was verified
  useEffect(() => {
    const iv = setInterval(async () => {
      try {
        const { user: u } = await standaloneApi.getMe();
        if (u.emailVerified) onVerified();
      } catch { /* ignore */ }
    }, 5000);
    return () => clearInterval(iv);
  }, [onVerified]);

  const handleResend = async () => {
    setSending(true); setError(""); setSent(false);
    try {
      await standaloneApi.resendVerification();
      setSent(true);
    } catch (err) {
      setError((err as Error).message);
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#f4f5f7", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <div className="bg-white rounded-2xl shadow-lg p-10 text-center max-w-md w-full mx-4" style={{ border: `1px solid ${C.g200}` }}>
        <div className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{ background: "#fffbeb" }}>
          <Send size={28} style={{ color: "#d97706" }} />
        </div>
        <p className="text-[15px] mb-6 leading-relaxed" style={{ color: C.g500 }}>
          We sent a verification link to <strong style={{ color: C.g800 }}>{user.name || user.email}</strong>. Please check your inbox and click the link to activate your account.
        </p>
        {sent && <p className="text-[14px] mb-4 font-semibold" style={{ color: "#059669" }}>Verification email resent!</p>}
        {error && <p className="text-[14px] mb-4 font-semibold" style={{ color: C.red }}>{error}</p>}
        <button onClick={handleResend} disabled={sending} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-[15px] font-semibold text-white mb-3" style={{ background: C.navy, opacity: sending ? 0.6 : 1 }}>
          {sending ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <><Send size={16} /> Resend Verification Email</>}
        </button>
        <div>
          <button onClick={onLogout} className="text-[13px] font-semibold mt-2" style={{ color: C.g400 }}>Sign out</button>
        </div>
      </div>
    </div>
  );
}

function StandaloneApp() {
  const [user, setUser] = useState<StandaloneUser | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("rei_token");
    if (token) {
      standaloneApi.getMe()
        .then(({ user: u }) => { setUser(u); setChecking(false); })
        .catch(() => { localStorage.removeItem("rei_token"); setChecking(false); });
    } else {
      setChecking(false);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("rei_token");
    setUser(null);
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#f4f5f7", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
        <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
        <Loader2 size={36} className="animate-spin" style={{ color: C.navy }} />
      </div>
    );
  }

  if (!user) {
    return <AuthScreen onAuth={setUser} />;
  }

  // Email verification gate
  if (user.emailVerified === false) {
    return <EmailVerifyScreen user={user} onVerified={() => setUser({ ...user, emailVerified: true })} onLogout={handleLogout} />;
  }

  // Logged in — show workspace with dummy contact (will be replaced after analysis)
  const dummyContact: Contact = { id: "", name: user.name, email: user.email, company: user.company };

  return <WorkspaceShell contact={dummyContact} standaloneUser={user} onLogout={handleLogout} appMode />;
}

/* ═══════════════════════════════════════════════
   WORKSPACE SHELL (shared between GHL and Standalone)
   ═══════════════════════════════════════════════ */
function WorkspaceShell({ contact, onBack, standaloneUser, onLogout, appMode }: { contact: Contact; onBack?: () => void; standaloneUser?: StandaloneUser; onLogout?: () => void; appMode?: boolean }) {
  const [tab, setTab] = useState("evaluate");
  const activeApi = appMode ? appApiAdapter : api;

  return (
    <AppModeCtx.Provider value={!!appMode}>
      <ApiCtx.Provider value={activeApi}>
        <ToastProvider>
          <div className="min-h-screen" style={{ background: C.navyBg, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
            <style>{`@keyframes toastIn{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}.animate-toastIn{animation:toastIn .3s ease-out}*{-webkit-font-smoothing:antialiased}::-webkit-scrollbar{width:6px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:${C.g300};border-radius:3px}`}</style>
            <div className="max-w-[1280px] mx-auto">
              <div className="flex items-center justify-between px-8 py-4 bg-white sticky top-0 z-50" style={{ borderBottom: `2px solid ${C.g200}` }}>
                <div className="flex items-center gap-4">
                  {onBack && (
                    <button onClick={onBack} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-semibold hover:opacity-80" style={{ color: C.navy, background: C.navyFaint }}>
                      <ChevronLeft size={16} /> Back
                    </button>
                  )}
                  <img src="/logo.png" alt="REI Software" className="h-10 object-contain" /><div className="w-px h-8" style={{ background: C.g200 }} /><span className="text-[18px] font-semibold" style={{ color: C.navy }}>Offer Analysis</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1 p-1 rounded-xl">{TABS.map(t => { const Icon = t.icon; const isA = tab === t.key; return <button key={t.key} onClick={() => setTab(t.key)} className="flex items-center gap-2.5 px-5 py-2.5 rounded-xl text-[14px] font-semibold" style={{ background: isA ? "white" : "transparent", color: isA ? C.navy : C.g500, boxShadow: isA ? "0 1px 4px rgba(0,0,0,0.08)" : "none" }}><Icon size={18} /><span className="hidden sm:inline">{t.label}</span></button>; })}</div>
                  {standaloneUser && onLogout && (
                    <button onClick={onLogout} className="text-[12px] px-3 py-1.5 rounded-lg" style={{ border: `1px solid ${C.g200}`, color: C.g500 }}>Sign Out</button>
                  )}
                </div>
              </div>
              {appMode && <ContactHeader contact={contact} />}
              <div className="px-8 py-6">
                {tab === "evaluate" && <EvaluateDealTab contactId={contact.id} />}
                {tab === "reports" && <ReportsTab contactId={contact.id} />}
                {tab === "lois" && <LOIsTab contactId={contact.id} />}
                {tab === "templates" && <TemplatesTab />}
                {tab === "settings" && <SettingsTab />}
              </div>
            </div>
          </div>
        </ToastProvider>
      </ApiCtx.Provider>
    </AppModeCtx.Provider>
  );
}

/* ═══════════════════════════════════════════════
   EXPORTS — each app is a separate entry point
   ═══════════════════════════════════════════════ */
export { GHLApp, StandaloneApp };

// Default export auto-detects mode (for dev convenience)
export default function App() {
  const p = new URLSearchParams(window.location.search);
  const isGHL = !!(p.get("locationId") || p.get("location_id") || p.get("contactId") || p.get("contact_id") || p.get("ssoKey"));

  if (isGHL) return <GHLApp />;
  return <StandaloneApp />;
}
